import React, { useCallback, useRef, useState, useMemo, useEffect } from "react";

import { useParams, Link, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
  Node,
  Edge,
  Panel,
  NodeProps,
  Connection,
  OnConnect,
  Handle,
  Position,
  BackgroundVariant,
  SelectionMode,
} from "reactflow";
import "reactflow/dist/style.css";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles, User, Bot, Zap, Cloud, Settings as SettingsIcon, BookOpen, Cpu, Database, Code, Trash2, Play, Pause, Square, Save, ArrowRight, ArrowLeft, ChevronLeft, Share2, Lock, Globe, Copy, Check, SlidersHorizontal, Split, Wand2
} from "lucide-react";

import { cn } from "@/lib/utils";
import { toast } from "@/components/ui/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/blueprints/ui/OSAwareDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/blueprints/ui/OSAwareAlertDialog";

import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { executeBlueprint, ExecutionContext, resumeBlueprint, continueAgenticFlow, continueBlueprintFrom } from "@/lib/blueprintExecutor";
import { proposeBlueprintPatch, applyBlueprintPatch } from "@/lib/blueprintEvolver";
import { useSupabaseStore } from "@/store/SupabaseStore";
import { ScrollArea } from "@/components/ui/scroll-area";
import { initialNodes as defaultInitialNodes, initialEdges as defaultInitialEdges } from "@/lib/blueprintDefaults";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider"; // Re-adding this import to ensure it's present
import { Blueprint } from "@/types";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useKnowledgeBaseCore } from "@/hooks/useKnowledgeBaseCore";
import { useMCPServices } from "@/hooks/supabase";
import ModelSelector from '@/components/chat/ModelSelector';
import { ShareBlueprintDialog } from "@/components/blueprints/ShareBlueprintDialog";
import { useOrganizations } from "@/hooks/useOrganizations";
import { getAllowlistPrefixes, isAgentGuardEnabled, isAgentOperationAllowed } from "@/lib/agentAccess";
import { getTrustEnabled } from "@/lib/trust";
import AgentTheater from '@/components/blueprints/AgentTheater';
import ThinkingDots from '@/components/blueprints/ThinkingDots';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface BlueprintCanvasProps {
  blueprintIdOverride?: string;
  embeddedInMapleOS?: boolean;
  onRequestClose?: () => void;
  onOpenBlueprint?: (id: string) => void;
}

const MAX_AGENTIC_AGENTS = 10;
const MAX_AGENTIC_TURNS = 60;

// Use `sb` for tables that may not yet exist in the generated Supabase TS types.
const sb = supabase as any;

const NODE_DEFS = [
  { type: "start", title: "Start", subtitle: "Blueprint begins here", icon: <ArrowRight className="h-6 w-6 text-white" />, color: "from-green-400 to-green-600", bg: "bg-green-50 dark:bg-green-900/30", accent: "bg-green-500 dark:bg-green-600", accentColor: "hsl(var(--color-green-600))" },
  { type: "end", title: "End", subtitle: "Blueprint ends here", icon: <ArrowLeft className="h-6 w-6 text-white" />, color: "from-red-400 to-red-600", bg: "bg-red-50 dark:bg-red-900/30", accent: "bg-red-500 dark:bg-red-600", accentColor: "hsl(var(--color-red-600))" },
  { type: "system-prompt", title: "System Prompt", subtitle: "Instructions for the AI", icon: <Sparkles className="h-6 w-6 text-white" />, color: "from-yellow-400 to-yellow-600", bg: "bg-yellow-50 dark:bg-yellow-900/30", accent: "bg-yellow-500 dark:bg-yellow-600", accentColor: "hsl(var(--color-yellow-500))" },
  { type: "user-input", title: "User Input", subtitle: "Data from the user", icon: <User className="h-6 w-6 text-white" />, color: "from-blue-400 to-blue-600", bg: "bg-blue-50 dark:bg-blue-900/30", accent: "bg-blue-500 dark:bg-blue-600", accentColor: "hsl(var(--color-blue-500))" },
  { type: "model-selector", title: "Model Selector", subtitle: "Choose an AI model", icon: <SlidersHorizontal className="h-6 w-6 text-white" />, color: "from-slate-400 to-slate-600", bg: "bg-slate-50 dark:bg-slate-800", accent: "bg-slate-500 dark:bg-slate-600", accentColor: "hsl(var(--color-slate-500))" },
  { type: "ai-response", title: "AI Response", subtitle: "The AI's generated reply", icon: <Bot className="h-6 w-6 text-white" />, color: "from-purple-400 to-purple-600", bg: "bg-purple-50 dark:bg-purple-900/30", accent: "bg-purple-500 dark:bg-purple-600", accentColor: "hsl(var(--color-purple-500))" },
  { type: "agentic-flow", title: "Agentic Flow", subtitle: "Two agents debate for N rounds", icon: <Wand2 className="h-6 w-6 text-white" />, color: "from-fuchsia-400 to-fuchsia-700", bg: "bg-fuchsia-50 dark:bg-fuchsia-900/30", accent: "bg-fuchsia-600 dark:bg-fuchsia-700", accentColor: "hsl(var(--color-fuchsia-600))" },
  { type: "user-decision", title: "User Decision", subtitle: "Pause and collect input", icon: <Lock className="h-6 w-6 text-white" />, color: "from-amber-400 to-amber-600", bg: "bg-amber-50 dark:bg-amber-900/30", accent: "bg-amber-500 dark:bg-amber-600", accentColor: "hsl(var(--color-amber-500))" },
  { type: "action", title: "Action", subtitle: "Trigger a process", icon: <Zap className="h-6 w-6 text-white" />, color: "from-pink-400 to-pink-600", bg: "bg-pink-50 dark:bg-pink-900/30", accent: "bg-pink-500 dark:bg-pink-600", accentColor: "hsl(var(--color-pink-500))" },
  { type: "api-call", title: "API Call", subtitle: "Fetch from an external API", icon: <Cloud className="h-6 w-6 text-white" />, color: "from-cyan-400 to-cyan-600", bg: "bg-cyan-50 dark:bg-cyan-900/30", accent: "bg-cyan-500 dark:bg-cyan-600", accentColor: "hsl(var(--color-cyan-500))" },
  { type: "configuration", title: "Configuration", subtitle: "Set parameters", icon: <SettingsIcon className="h-6 w-6 text-white" />, color: "from-green-400 to-green-600", bg: "bg-green-50 dark:bg-green-900/30", accent: "bg-green-500 dark:bg-green-600", accentColor: "hsl(var(--color-green-600))" },
  { type: "knowledge-base", title: "Knowledge Base", subtitle: "Reference documents", icon: <BookOpen className="h-6 w-6 text-white" />, color: "from-orange-400 to-orange-600", bg: "bg-orange-50 dark:bg-orange-900/30", accent: "bg-orange-500 dark:bg-orange-600", accentColor: "hsl(var(--color-orange-500))" },
  { type: "data", title: "Data", subtitle: "Provide static data", icon: <Database className="h-6 w-6 text-white" />, color: "from-indigo-400 to-indigo-600", bg: "bg-indigo-50 dark:bg-indigo-900/30", accent: "bg-indigo-500 dark:bg-indigo-600", accentColor: "hsl(var(--color-indigo-500))" },
  { type: "mcp", title: "MCP", subtitle: "Machine-Callable Program", icon: <Cpu className="h-6 w-6 text-white" />, color: "from-teal-400 to-teal-600", bg: "bg-teal-50 dark:bg-teal-900/30", accent: "bg-teal-500 dark:bg-teal-600", accentColor: "hsl(var(--color-teal-500))" },
  { type: "mapleclaw-gateway", title: "MapleClaw Gateway", subtitle: "Send local control-plane events", icon: <Cpu className="h-6 w-6 text-white" />, color: "from-emerald-400 to-emerald-700", bg: "bg-emerald-50 dark:bg-emerald-900/30", accent: "bg-emerald-600 dark:bg-emerald-700", accentColor: "hsl(var(--color-emerald-600))" },
  { type: "intelligencexp", title: "IntelligenceXP", subtitle: "Search / Read / Index", icon: <Cpu className="h-6 w-6 text-white" />, color: "from-sky-400 to-sky-600", bg: "bg-sky-50 dark:bg-sky-900/30", accent: "bg-sky-500 dark:bg-sky-600", accentColor: "hsl(var(--color-sky-500))" },
  { type: "conditional", title: "Conditional", subtitle: "If/Else logic branch", icon: <Split className="h-6 w-6 text-white" />, color: "from-orange-400 to-orange-600", bg: "bg-orange-50 dark:bg-orange-900/30", accent: "bg-orange-500 dark:bg-orange-600", accentColor: "hsl(var(--color-orange-500))" },
];

const getNodeDef = (type: string) => NODE_DEFS.find((n) => n.type === type);

function BlueprintNode({ data, selected }: NodeProps) {
  const def = getNodeDef(data.type) || NODE_DEFS[0];
  const runtimeActive = Boolean((data as any).__runtimeActive);
  return (
    <div
      className={cn(
        "rounded-xl shadow-lg border-2 transition-all min-w-[180px] max-w-xs relative",
        def.bg,
        selected ? "border-primary scale-[1.03]" : "border-transparent",
        runtimeActive && "ring-2 ring-primary/60 shadow-[0_0_32px_rgba(234,56,76,0.25)] animate-pulse"
      )}
      style={{ boxShadow: selected ? "0 4px 24px 0 rgba(234,56,76,0.12)" : "0 2px 8px 0 rgba(0,0,0,0.06)" }}
    >
      {data.type !== 'start' && <Handle type="target" position={Position.Left} id="target" style={{ background: "#ea384c", width: 12, height: 12, borderRadius: 6, left: -8, top: "50%", border: "2px solid #fff" }} />}
      <div className="flex items-center gap-3 p-3">
        <div className={cn("rounded-full p-2 shadow", def.accent, "bg-gradient-to-br", def.color)}>{def.icon}</div>
        <div>
          <div className="font-bold text-base leading-tight">{data.label || def.title}</div>
          <div className="text-xs text-muted-foreground">{data.description || def.subtitle}</div>
        </div>
      </div>
      {data.type !== 'end' && <Handle type="source" position={Position.Right} id="source" style={{ background: "#ea384c", width: 12, height: 12, borderRadius: 6, right: -8, top: "50%", border: "2px solid #fff" }} />}
    </div>
  );
}

function ConditionalNode({ data, selected }: NodeProps) {
  const def = getNodeDef(data.type) || NODE_DEFS[0];
  return (
    <div className={cn("rounded-xl shadow-lg border-2 transition-all min-w-[180px] max-w-xs relative", def.bg, selected ? "border-primary scale-[1.03]" : "border-transparent")} style={{ boxShadow: selected ? "0 4px 24px 0 rgba(234,56,76,0.12)" : "0 2px 8px 0 rgba(0,0,0,0.06)" }} >
      <Handle type="target" position={Position.Left} id="target" style={{ background: "#ea384c", width: 12, height: 12, borderRadius: 6, left: -8, top: "50%", border: "2px solid #fff" }} />
      <div className="flex items-center gap-3 p-3">
        <div className={cn("rounded-full p-2 shadow", def.accent, "bg-gradient-to-br", def.color)}>{def.icon}</div>
        <div>
          <div className="font-bold text-base leading-tight">{data.label || def.title}</div>
          <div className="text-xs text-muted-foreground">{data.description || def.subtitle}</div>
        </div>
      </div>
      <Handle type="source" position={Position.Right} id="true" style={{ background: "#22c55e", width: 12, height: 12, borderRadius: 6, right: -8, top: "33%", border: "2px solid #fff" }} />
      <div className="absolute right-[-35px] top-[33%] -translate-y-1/2 text-xs font-semibold text-green-600">True</div>
      <Handle type="source" position={Position.Right} id="false" style={{ background: "#ef4444", width: 12, height: 12, borderRadius: 6, right: -8, top: "66%", border: "2px solid #fff" }} />
      <div className="absolute right-[-38px] top-[66%] -translate-y-1/2 text-xs font-semibold text-red-600">False</div>
    </div>
  );
}

const nodeTypes = {
  ...Object.fromEntries(NODE_DEFS.map((def) => [def.type, BlueprintNode])),
  conditional: ConditionalNode,
};

function NodePalette({
  onDragStart,
  embeddedInMapleOS = false,
}: {
  onDragStart: (event: React.DragEvent, type: string) => void;
  embeddedInMapleOS?: boolean;
}) {
  return (
    <div className="h-full w-full flex flex-col">
      <div
        className={cn("rounded-xl shadow-md border bg-gradient-to-br from-background/80 to-muted/60 dark:from-background/90 dark:to-muted/40 p-4 m-3 flex flex-col gap-3 h-full", embeddedInMapleOS ? "min-h-0" : "")}
        style={{
          minHeight: embeddedInMapleOS ? '100%' : 'calc(100vh - 80px - 24px)',
          backdropFilter: 'blur(2px)',
        }}
      >
        <div className="font-semibold text-sm mb-2 text-foreground/80">Nodes</div>
        {NODE_DEFS.filter(n => n.type !== "start" && n.type !== "end").map((node) => (
          <div key={node.type} className={cn("flex items-center gap-3 px-3 py-2 rounded-lg group transition-all cursor-grab border border-transparent hover:border-accent", "bg-background/80 dark:bg-background/60 shadow-sm")} draggable onDragStart={(event) => onDragStart(event, node.type)} style={{ borderLeft: `8px solid ${node.accentColor}`, boxShadow: "0 1px 4px 0 rgba(0,0,0,0.04)" }}>
            <div className={cn("rounded-full p-2 shadow", node.accent, "bg-gradient-to-br", node.color)}>{node.icon}</div>
            <div>
              <div className="font-bold text-sm text-foreground">{node.title}</div>
              <div className="text-xs text-muted-foreground">{node.subtitle}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function hasPathFromStartToEnd(nodes: Node[], edges: Edge[]): boolean {
  const startNode = nodes.find(n => n.type === 'start');
  const endNode = nodes.find(n => n.type === 'end');
  if (!startNode || !endNode) return false;

  const adj: Record<string, string[]> = {};
  nodes.forEach(n => { adj[n.id] = []; });
  edges.forEach(e => { if (adj[e.source]) adj[e.source].push(e.target); });
  
  const visited = new Set<string>();
  const queue = [startNode.id];
  while (queue.length > 0) {
    const curr = queue.shift()!;
    if (curr === endNode.id) return true;
    if (!visited.has(curr)) {
      visited.add(curr);
      for (const neighbor of adj[curr] || []) {
        if (!visited.has(neighbor)) {
          queue.push(neighbor);
        }
      }
    }
  }
  return false;
}



/**
 * If the run history contains deep-research-seeds events but no corresponding
 * blossom events, inject synthetic blossom entries so the Cortex renders them.
 * This allows saved runs to show blossoms even when Auto Blossom was off during execution.
 */
function injectBlossomEventsFromSeeds(history: any[]): any[] {
  if (!Array.isArray(history) || !history.length) return history;

  // Collect all persisted seeds
  const allSeeds: { title: string; url: string }[] = [];
  for (const h of history) {
    if (h?.kind === 'deep-research-seeds' && Array.isArray(h.seeds)) {
      for (const s of h.seeds) {
        if (s?.title && s?.url) allSeeds.push(s);
      }
    }
  }
  if (!allSeeds.length) return history;

  // Check which seeds already have blossom events
  const existingBlossomTitles = new Set<string>();
  for (const h of history) {
    if (h?.kind === 'blossom' && h.kbTitle) existingBlossomTitles.add(String(h.kbTitle));
  }

  const newSeeds = allSeeds.filter((s) => !existingBlossomTitles.has(s.title));
  if (!newSeeds.length) return history;

  // Inject synthetic blossom events
  const syntheticEvents = newSeeds.map((seed) => ({
    nodeId: `blossom:${seed.title}`,
    message: `Blossom: ${seed.title}`,
    timestamp: new Date().toISOString(),
    role: 'system',
    lane: 'left',
    kind: 'blossom',
    kbTitle: seed.title,
    kbUrl: seed.url,
  }));

  return [...history, ...syntheticEvents];
}

function normalizeRunHistory(history: any[]): any[] {
  return injectBlossomEventsFromSeeds(Array.isArray(history) ? history : []);
}

function getRunSnapshotNodes(run: any, fallbackNodes: Node[]): Node[] {
  return Array.isArray(run?.nodes_snapshot) && run.nodes_snapshot.length
    ? (run.nodes_snapshot as Node[])
    : fallbackNodes;
}

function getRunHistoryCount(run: any): number {
  return Array.isArray(run?.history) ? run.history.length : 0;
}

function getRunLastMessage(run: any): string {
  const history = normalizeRunHistory(run?.history || []);
  return history.length ? String(history[history.length - 1]?.message || '') : '';
}

const BlueprintCanvas = ({
  blueprintIdOverride,
  embeddedInMapleOS = false,
  onRequestClose,
  onOpenBlueprint,
}: BlueprintCanvasProps) => {
  const params = useParams<{ blueprintId: string }>();
  const blueprintId = blueprintIdOverride ?? params.blueprintId;
  const navigate = useNavigate();
  const { user } = useAuth();
  const [blueprint, setBlueprint] = useState<Blueprint & { user_id: string } | null>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesState] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const models = useSupabaseStore(state => state.models);
  const { knowledgeBases, fetchKnowledgeBases } = useKnowledgeBaseCore();
  const { services: mcpServices, getApiKey, refreshServices } = useMCPServices();
  const userApiKeys = useMemo(() => {
    const keys: Record<string, string> = {};
    for (const svc of mcpServices) {
      const k = getApiKey(svc.id);
      if (k) keys[svc.id] = k;
    }
    return keys;
  }, [mcpServices, getApiKey]);

  const { organizations } = useOrganizations();

  // Ownership + dirty state

  const [isOwner, setIsOwner] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [showLeaveConfirmDialog, setShowLeaveConfirmDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);

  // Uploading
  const [isUploading, setIsUploading] = useState(false);

  // Run dialog state
  const [runDialogOpen, setRunDialogOpen] = useState(false);
  const [runInitialInput, setRunInitialInput] = useState('');
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionResult, setExecutionResult] = useState<ExecutionContext | null>(null);
  const [activeTheaterNodeId, setActiveTheaterNodeId] = useState<string | null>(null);

  // Run controls (cooperative pause/stop)
  const [stopRequested, setStopRequested] = useState(false);
  const [pauseRequested, setPauseRequested] = useState(false);
  const runControlRef = useRef({ stop: false, pause: false });

  // Run option: auto-open the 3D Orchestration ("Cortex") view when a run begins.

  const [autoLoad3DCortexOnRun, setAutoLoad3DCortexOnRun] = useState(() => {
    try {
      return localStorage.getItem('bp:autoLoad3DCortexOnRun') === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('bp:autoLoad3DCortexOnRun', autoLoad3DCortexOnRun ? '1' : '0');
    } catch {
      // ignore
    }
  }, [autoLoad3DCortexOnRun]);

  // Run option: Semantic physics settings for the 3D Cortex.
  // Stored in localStorage so Orchestration3D can consume them when it opens.
  const [semanticPhysicsEnabled, setSemanticPhysicsEnabled] = useState(() => {
    try {
      return localStorage.getItem('orch3d.semanticEnabled') === '1';
    } catch {
      return false;
    }
  });
  const [semanticTemp, setSemanticTemp] = useState(() => {
    try {
      const v = Number(localStorage.getItem('orch3d.semanticTemp'));
      return Number.isFinite(v) ? v : 0.02;
    } catch {
      return 0.02;
    }
  });
  const [semanticG, setSemanticG] = useState(() => {
    try {
      const v = Number(localStorage.getItem('orch3d.semanticG'));
      return Number.isFinite(v) ? v : 25;
    } catch {
      return 25;
    }
  });
  const [semanticK, setSemanticK] = useState(() => {
    try {
      const v = Number(localStorage.getItem('orch3d.semanticK'));
      return Number.isFinite(v) ? v : 200;
    } catch {
      return 200;
    }
  });
  const [semanticTopK, setSemanticTopK] = useState(() => {
    try {
      const v = Number(localStorage.getItem('orch3d.topK'));
      return Number.isFinite(v) ? v : 6;
    } catch {
      return 6;
    }
  });
  const [semanticAffinityThreshold, setSemanticAffinityThreshold] = useState(() => {
    try {
      const v = Number(localStorage.getItem('orch3d.affinityThreshold'));
      return Number.isFinite(v) ? v : 0.05;
    } catch {
      return 0.05;
    }
  });
  const [semanticAnchorEnabled, setSemanticAnchorEnabled] = useState(() => {
    try {
      return localStorage.getItem('orch3d.anchorEnabled') !== '0';
    } catch {
      return true;
    }
  });
  const [semanticPromptText, setSemanticPromptText] = useState(() => {
    try {
      return localStorage.getItem('orch3d.promptText') || '';
    } catch {
      return '';
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('orch3d.semanticEnabled', semanticPhysicsEnabled ? '1' : '0');
      localStorage.setItem('orch3d.semanticTemp', String(semanticTemp));
      localStorage.setItem('orch3d.semanticG', String(semanticG));
      localStorage.setItem('orch3d.semanticK', String(semanticK));
      localStorage.setItem('orch3d.topK', String(semanticTopK));
      localStorage.setItem('orch3d.affinityThreshold', String(semanticAffinityThreshold));
      localStorage.setItem('orch3d.anchorEnabled', semanticAnchorEnabled ? '1' : '0');
      localStorage.setItem('orch3d.promptText', semanticPromptText);
    } catch {
      // ignore
    }
  }, [
    semanticPhysicsEnabled,
    semanticTemp,
    semanticG,
    semanticK,
    semanticTopK,
    semanticAffinityThreshold,
    semanticAnchorEnabled,
    semanticPromptText,
  ]);

  // Agent follow-ups / decisions

  const [decisionValue, setDecisionValue] = useState('');
  const [followupText, setFollowupText] = useState('');
  const [followupRounds, setFollowupRounds] = useState(1);

  // Context menu
  const [menu, setMenu] = useState<{ id: string; type: 'node' | 'edge'; x: number; y: number } | null>(null);
  const contextMenuTriggerRef = useRef<HTMLButtonElement>(null);
  const [copied, setCopied] = useState(false);

  // AI Build + Auto Evolve
  const [aiBuildOpen, setAiBuildOpen] = useState(false);
  const [aiBuildPrompt, setAiBuildPrompt] = useState(
    'Build me a blueprint that fetches the weather and then discuss it with another agent'
  );
  const [autoEvolveEnabled, setAutoEvolveEnabled] = useState(false);
  const autoEvolveStopRef = useRef(false);

  // Per-blueprint Trust disable: when true, Trust prechecks/postchecks are skipped for this blueprint.
  const [blueprintTrustDisabled, setBlueprintTrustDisabled] = useState(() => {
    try {
      return localStorage.getItem(`bp:${blueprintId}:trust_disabled`) === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    if (!blueprintId) return;
    try {
      localStorage.setItem(`bp:${blueprintId}:trust_disabled`, blueprintTrustDisabled ? '1' : '0');
    } catch {
      // ignore
    }
  }, [blueprintId, blueprintTrustDisabled]);

  // Right-mouse drag selection (Epic/Unreal style), with left-drag panning.
  const [rightSelectBox, setRightSelectBox] = useState<null | {
    startX: number;
    startY: number;
    endX: number;
    endY: number;
  }>(null);
  const rightSelectingRef = useRef(false);

  // Keep refs to latest graph for async operations
  const nodesRef = useRef<Node[]>([]);
  const edgesRef = useRef<Edge[]>([]);
  useEffect(() => {
    nodesRef.current = nodes as any;
  }, [nodes]);
  useEffect(() => {
    edgesRef.current = edges as any;
  }, [edges]);

  // Run persistence
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const runIsPaused = Boolean(activeRunId) && !isExecuting && (executionResult as any)?.awaiting?.type === 'manual-pause';
  const runIsActive = Boolean(activeRunId) && (Boolean(isExecuting) || runIsPaused);
  const activeRunIdRef = useRef<string | null>(null);
  useEffect(() => {
    activeRunIdRef.current = activeRunId;
  }, [activeRunId]);

  useEffect(() => {
    runControlRef.current = { stop: stopRequested, pause: pauseRequested };
  }, [stopRequested, pauseRequested]);

  const [runsOpen, setRunsOpen] = useState(false);
  const [runs, setRuns] = useState<any[]>([]);
  const [selectedRun, setSelectedRun] = useState<any | null>(null);

  const [completedRunRecord, setCompletedRunRecord] = useState<any | null>(null);
  const [scoutEnabled, setScoutEnabled] = useState(false);
  const [scoutKeywords, setScoutKeywords] = useState<string[]>([]);

  // Persist progress helper is defined later, but some helpers are defined early.

  // Use a ref to avoid TDZ / initialization ordering issues.
  const persistRunProgressRef = useRef<
    (ctx: ExecutionContext, extras?: { status?: string; nodes?: Node[]; edges?: Edge[] }) => Promise<void>
  >(async () => {});

  // Auto-blossom: spawn debates automatically for newly created KB sources.
  const [autoBlossomEnabled, setAutoBlossomEnabled] = useState(false);
  const [blossomConcurrency, setBlossomConcurrency] = useState(2);
  const blossomStopRef = useRef(false);
  const blossomPumpRunningRef = useRef(false);
  const blossomInFlightRef = useRef(0);
  const blossomQueueRef = useRef<{ title: string; url: string }[]>([]);
  const blossomedRef = useRef<Set<string>>(new Set());
  const [blossomStats, setBlossomStats] = useState({ queued: 0, inFlight: 0, completed: 0 });

  const updateBlossomStats = useCallback(() => {
    setBlossomStats((prev) => ({
      ...prev,
      queued: blossomQueueRef.current.length,
      inFlight: blossomInFlightRef.current,
    }));
  }, []);

  const resetBlossoms = useCallback(() => {
    blossomStopRef.current = true;
    blossomQueueRef.current = [];
    blossomedRef.current = new Set();
    blossomInFlightRef.current = 0;
    blossomPumpRunningRef.current = false;
    setBlossomStats({ queued: 0, inFlight: 0, completed: 0 });
  }, []);

  const requestPause = () => {
    setPauseRequested(true);
    toast({ title: 'Pause requested', description: 'The run will pause at the next safe point.' });
  };

  const requestStop = () => {
    setStopRequested(true);
    toast({ title: 'Stop requested', description: 'The run will stop at the next safe point.' });
  };

  const resetRunControls = () => {
    setStopRequested(false);
    setPauseRequested(false);
  };

  const stopAndClearActiveRun = async () => {
    // Cooperative stop: request stop and mark run record if we have one.
    setStopRequested(true);
    setPauseRequested(false);

    const id = activeRunIdRef.current;
    if (id) {
      const { error } = await sb
        .from('blueprint_runs')
        .update({ status: 'stopped', updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) {
        console.warn('Failed to mark run stopped:', error.message);
      }
    }

    activeRunIdRef.current = null;
    setActiveRunId(null);
    setExecutionResult(null);
    setIsExecuting(false);
    resetRunControls();
    setRunDialogOpen(false);
  };

  const enqueueSeedsFromContext = useCallback((ctx: ExecutionContext | null) => {
    if (!ctx) return;

    // Primary: read seeds from in-memory variables (set during execution)
    const varSeeds: any[] = Array.isArray((ctx.variables as any)?.deepResearchSeeds) ? (ctx.variables as any).deepResearchSeeds : [];

    // Fallback: read seeds from persisted history events (survives DB round-trips)
    const historySeeds: any[] = [];
    if (Array.isArray(ctx.history)) {
      for (const h of ctx.history) {
        if (h?.kind === 'deep-research-seeds' && Array.isArray(h.seeds)) {
          for (const s of h.seeds) historySeeds.push(s);
        }
      }
    }

    // Merge — variable seeds first, then any extras from history
    const seen = new Set<string>();
    const allSeeds: any[] = [];
    for (const s of [...varSeeds, ...historySeeds]) {
      const title = String(s?.title || '').trim();
      const url = String(s?.url || '').trim();
      if (!title || !url) continue;
      const key = `${title}::${url}`;
      if (seen.has(key)) continue;
      seen.add(key);
      allSeeds.push(s);
    }

    let added = 0;
    for (const s of allSeeds) {
      const title = String(s?.title || '').trim();
      const url = String(s?.url || '').trim();
      if (!title || !url) continue;
      const key = `${title}::${url}`;
      if (blossomedRef.current.has(key)) continue;
      blossomQueueRef.current.push({ title, url });
      added++;
    }

    if (added) updateBlossomStats();
  }, [updateBlossomStats]);

  const appendToHistoryLive = useCallback((entries: any[]) => {
    if (!entries.length) return;
    setExecutionResult((prev) => {
      if (!prev) return prev;
      const next = { ...prev, history: [...(prev.history || []), ...entries] } as any;
      // Persist playback timeline as we blossom.
      void persistRunProgressRef.current(next);
      return next;
    });
  }, []);

  const runSingleBlossom = useCallback(async (seed: { title: string; url: string }) => {
    const baseTs = new Date().toISOString();
    const blossomNodeId = `blossom:${seed.title}`;

    // Emit a blossom event so Orchestration3D renders a star birth.
    appendToHistoryLive([
      {
        nodeId: blossomNodeId,
        message: `Blossom: ${seed.title}`,
        timestamp: baseTs,
        role: 'system',
        lane: 'left',
        kind: 'blossom',
        kbTitle: seed.title,
        kbUrl: seed.url,
      },
    ]);

    // Run a small multi-agent debate focused on this source.
    const systemPrefix =
      `You are debating and synthesizing research directions based on ONE source.\n` +
      `Source title: ${seed.title}\nSource URL: ${seed.url}\n\n` +
      `Rules:\n- Be evidence-focused; call out uncertainty.\n- Do not claim cures.\n- End with 2–4 concrete next search queries (to potentially create new KBs).`;

    const prompt =
      `Read and reason from this source: ${seed.title} (${seed.url}).\n` +
      `Discuss what it suggests, what it misses, and propose next steps.`;

    const nodeId = blossomNodeId;
    const tmpNode: any = {
      id: nodeId,
      type: 'agentic-flow',
      data: {
        type: 'agentic-flow',
        label: `Blossom Debate: ${seed.title}`,
        description: 'Auto-blossom debate',
        agents: [
          {
            id: 'agent1',
            name: 'Scout',
            persona: 'Summarize key claims and evidence from the source. Identify reliability and limitations.',
            modelId: '',
          },
          {
            id: 'agent2',
            name: 'Skeptic',
            persona: 'Challenge assumptions, highlight missing controls/limitations, propose alternative interpretations.',
            modelId: '',
          },
          {
            id: 'agent3',
            name: 'Planner',
            persona: 'Turn insights into actionable next steps: hypotheses, experiments, and next search queries.',
            modelId: '',
          },
        ],
        rounds: 3,
        modelSource: 'inherit',
        retrieval: {
          useLocal: true,
          kbId: (executionResult as any)?.variables?.deepResearchKbId || '',
          topK: 5,
          threshold: 0.75,
          showSources: true,
          autoIndex: false,
        },
        returnMode: 'summary',
      },
    };

    const localNodes: any[] = [tmpNode];
    const localEdges: any[] = [];

    // This mini-run is independent, but we append its history into the main run.
    const result = await executeBlueprint(localNodes as any, localEdges as any, prompt, models, mcpServices, userApiKeys);

    const stamped = (result.history || []).map((h: any) => {
      const lane = String(h?.lane || 'left');
      const role = String(h?.role || 'system');
      const scopedLane = role === 'agent' && lane !== 'left' ? `${nodeId}/${lane}` : lane;
      return {
        ...h,
        nodeId: nodeId,
        lane: scopedLane,
        kbTitle: seed.title,
        kbUrl: seed.url,
      };
    });

    appendToHistoryLive(stamped);

    // If the debate created more seeds (e.g., via downstream Deep Research in the future), enqueue them.
    enqueueSeedsFromContext(result);
  }, [appendToHistoryLive, enqueueSeedsFromContext, executionResult, mcpServices, models, userApiKeys]);

  const pumpBlossoms = useCallback(async () => {
    if (!autoBlossomEnabled) return;
    if (blossomPumpRunningRef.current) return;

    blossomPumpRunningRef.current = true;
    blossomStopRef.current = false;

    try {
      while (!blossomStopRef.current) {
        if (blossomInFlightRef.current >= Math.max(1, blossomConcurrency)) {
          await new Promise((r) => setTimeout(r, 120));
          continue;
        }

        const next = blossomQueueRef.current.shift();
        updateBlossomStats();

        if (!next) {
          await new Promise((r) => setTimeout(r, 250));
          continue;
        }

        const key = `${next.title}::${next.url}`;
        if (blossomedRef.current.has(key)) continue;
        blossomedRef.current.add(key);

        blossomInFlightRef.current++;
        updateBlossomStats();

        (async () => {
          try {
            await runSingleBlossom(next);
            setBlossomStats((s) => ({ ...s, completed: s.completed + 1 }));
          } catch (e: any) {
            appendToHistoryLive([
              {
                nodeId: `${next.title}`,
                message: `Blossom failed: ${next.title} — ${e?.message || String(e)}`,
                timestamp: new Date().toISOString(),
                role: 'system',
                lane: 'left',
              },
            ]);
          } finally {
            blossomInFlightRef.current = Math.max(0, blossomInFlightRef.current - 1);
            updateBlossomStats();
          }
        })();
      }
    } finally {
      blossomPumpRunningRef.current = false;
    }
  }, [autoBlossomEnabled, blossomConcurrency, runSingleBlossom, appendToHistoryLive, updateBlossomStats]);

  // Start/stop behavior when toggled.
  useEffect(() => {
    if (!autoBlossomEnabled) {
      blossomStopRef.current = true;
      return;
    }

    // enabling: enqueue from current context and start pump
    enqueueSeedsFromContext(executionResult);
    void pumpBlossoms();
  }, [autoBlossomEnabled, enqueueSeedsFromContext, executionResult, pumpBlossoms]);

  // When the main run updates, enqueue new seeds.
  useEffect(() => {
    if (!autoBlossomEnabled) return;
    enqueueSeedsFromContext(executionResult);
  }, [autoBlossomEnabled, executionResult, enqueueSeedsFromContext]);

  const loadRuns = useCallback(async () => {
    if (!blueprintId || !user?.id) return;
    const { data, error } = await sb
      .from('blueprint_runs')
      .select('id, created_at, updated_at, title, status, last_cursor, initial_input, history, nodes_snapshot, edges_snapshot')
      .eq('blueprint_id', blueprintId)
      .order('created_at', { ascending: false });
    if (error) {
      toast({ title: 'Failed to load runs', description: error.message, variant: 'destructive' });
      return;
    }
    const nextRuns = data || [];
    setRuns(nextRuns);
    setSelectedRun((current) => {
      if (current?.id) {
        const matchingCurrent = nextRuns.find((run) => run.id === current.id);
        if (matchingCurrent) return matchingCurrent;
      }
      if (completedRunRecord?.id) {
        const matchingCompleted = nextRuns.find((run) => run.id === completedRunRecord.id);
        if (matchingCompleted) return matchingCompleted;
      }
      return nextRuns[0] || null;
    });
  }, [blueprintId, completedRunRecord?.id, user?.id]);

  const ensureRun = useCallback(async (initialInput?: string) => {
    if (!blueprintId || !user?.id) return null;
    if (activeRunIdRef.current) return activeRunIdRef.current;

    const title = (() => {
      const base = (blueprint?.name || 'Blueprint').trim();
      const suffix = new Date().toLocaleString();
      return `${base} — Run (${suffix})`;
    })();

    const { data, error } = await sb
      .from('blueprint_runs')
      .insert({
        blueprint_id: blueprintId,
        user_id: user.id,
        title,
        initial_input: initialInput || '',
        status: 'running',
        last_cursor: 0,
        history: [],
        nodes_snapshot: nodesRef.current,
        edges_snapshot: edgesRef.current,
      })
      .select('id')
      .single();

    if (error) {
      toast({ title: 'Failed to start run', description: error.message, variant: 'destructive' });
      return null;
    }

    const id = data?.id || null;
    activeRunIdRef.current = id;
    setActiveRunId(id);
    return id;
  }, [blueprint?.name, blueprintId, user?.id]);

  const persistRunProgress = useCallback(async (ctx: ExecutionContext, extras?: { status?: string; nodes?: Node[]; edges?: Edge[] }) => {
    const runId = activeRunIdRef.current;
    if (!runId) return;

    const payload: any = {
      updated_at: new Date().toISOString(),
      history: ctx.history || [],
      last_cursor: Array.isArray(ctx.history) ? ctx.history.length : 0,
      status: extras?.status || (ctx.error ? 'failed' : ctx.awaiting ? 'paused' : 'running'),
    };
    if (extras?.nodes) payload.nodes_snapshot = extras.nodes;
    if (extras?.edges) payload.edges_snapshot = extras.edges;

    const { error } = await sb.from('blueprint_runs').update(payload).eq('id', runId);
    if (error) {
      // non-fatal
      console.warn('Failed to persist run progress:', error.message);
    }
  }, []);

  useEffect(() => {
    persistRunProgressRef.current = persistRunProgress;
  }, [persistRunProgress]);

  useEffect(() => {
    setRunInitialInput(localStorage.getItem(`blueprint_input_${blueprintId}`) || '');
  }, [blueprintId]);

  useEffect(() => {
    fetchKnowledgeBases();
  }, [fetchKnowledgeBases]);

  useEffect(() => {
    refreshServices();
  }, [refreshServices]);

  useEffect(() => {
    const fetchBlueprint = async () => {
      if (!blueprintId) return;
      setLoading(true);
      const { data, error } = await supabase.from('blueprints').select('*').eq('id', blueprintId).single();
      if (error) {
        toast({ title: 'Error loading blueprint', description: error.message, variant: 'destructive' });
      } else if (data) {
        setBlueprint(data as Blueprint & { user_id: string });
        setNodes((data as any).data?.nodes || defaultInitialNodes);
        setEdges((data as any).data?.edges || defaultInitialEdges);
        setIsOwner((data as any).user_id === user?.id);
      }
      setLoading(false);
    };
    fetchBlueprint();
  }, [blueprintId, setNodes, setEdges, user]);

  const selectedNode = useMemo(() => nodes.find((n) => n.id === selectedNodeId) || null, [selectedNodeId, nodes]);
  useEffect(() => { setIsPanelOpen(!!selectedNodeId); }, [selectedNodeId]);

  const onDragOver = useCallback((event: React.DragEvent) => { event.preventDefault(); event.dataTransfer.dropEffect = "move"; }, []);
  
  const onDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    if (!isOwner) return;
    const type = event.dataTransfer.getData("application/reactflow");
    if (!type || !reactFlowInstance) return;
    const position = reactFlowInstance.project({ x: event.clientX - (reactFlowWrapper.current?.getBoundingClientRect().left || 0), y: event.clientY - (reactFlowWrapper.current?.getBoundingClientRect().top || 0) });
    const def = getNodeDef(type);

    const baseData: any = { type, label: def?.title || type, description: def?.subtitle || "", content: "" };
    const defaultsByType: Record<string, any> = {
      intelligencexp: {
        mode: 'search',
        domain: 'files',
        querySource: 'user-input',
        customQuery: '',
        topK: 5,
        threshold: 0.75,
        kbId: '',
        pathSource: 'last-output',
        manualPath: '',
        indexAction: 'start',
        scopes: ['documents'],
      },
      'mapleclaw-gateway': {
        mode: 'send-message',
        sessionKey: 'main',
        payload: '{{lastOutput}}',
        awaitResponse: true,
        timeoutMs: 30000,
        tailLines: 50,
        processingRuntime: 'local',
        modelId: '',
        agentInstruction: '',
      },

      'agentic-flow': {
        agents: [
          {
            id: 'agent1',
            name: 'Agent 1',
            persona: 'You are Agent 1. Analyze and propose an initial position.',
            modelId: '',
          },
          {
            id: 'agent2',
            name: 'Agent 2',
            persona: 'You are Agent 2. Challenge assumptions and ask clarifying questions.',
            modelId: '',
          },
        ],
        agent1Persona: 'You are Agent 1. Analyze and propose an initial position.',
        agent2Persona: 'You are Agent 2. Challenge assumptions and ask clarifying questions.',
        rounds: 3,
        modelSource: 'inherit',
        modelA: '',
        modelB: '',
        retrieval: {
          useLocal: true,
          kbId: '',
          topK: 5,
          threshold: 0.75,
          showSources: true,
          autoIndex: false,
        },
        returnMode: 'last',
      },
      'user-decision': {
        prompt: 'Please provide input to continue.',
        variable: 'decisionInput',
        allowEmpty: false,
      },
      action: {
        actionType: 'json-extract',
        jsonPath: '',
        template: '',
        delimiter: '\\n',
      },
    };

    setNodes((nds) =>
      nds.concat({
        id: `${type}-${+new Date()}`,
        type,
        position,
        data: { ...baseData, ...(defaultsByType[type] || {}) },
      })
    );
    setIsDirty(true);
  }, [reactFlowInstance, setNodes, isOwner]);

  const onNodeDragStart = (event: React.DragEvent, type: string) => { event.dataTransfer.setData("application/reactflow", type); event.dataTransfer.effectAllowed = "move"; };
  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node) => { setSelectedNodeId(node.id); }, []);
  const onPaneClick = useCallback(() => {
    setSelectedNodeId(null);
    setMenu(null);
  }, []);

  const onPaneContextMenu = useCallback((event: React.MouseEvent) => {
    // Prevent the browser context menu on the canvas.
    event.preventDefault();
  }, []);

  const beginRightDragSelect = useCallback((event: React.MouseEvent) => {
    if (!isOwner) return;
    if (event.button !== 2) return;

    // Only start selection when dragging on the blank pane (not nodes/edges/UI).
    const t = event.target as HTMLElement | null;
    if (t?.closest?.('.react-flow__node, .react-flow__edge, .react-flow__panel, [data-radix-popper-content-wrapper]')) {
      return;
    }

    event.preventDefault();

    const wrap = reactFlowWrapper.current;
    if (!wrap) return;
    const r = wrap.getBoundingClientRect();
    const x = event.clientX - r.left;
    const y = event.clientY - r.top;

    rightSelectingRef.current = true;
    setRightSelectBox({ startX: x, startY: y, endX: x, endY: y });
  }, [isOwner]);

  const updateRightDragSelect = useCallback((event: MouseEvent) => {
    if (!rightSelectingRef.current) return;

    event.preventDefault();

    const wrap = reactFlowWrapper.current;
    if (!wrap) return;
    const r = wrap.getBoundingClientRect();
    const x = event.clientX - r.left;
    const y = event.clientY - r.top;

    setRightSelectBox((prev) => (prev ? { ...prev, endX: x, endY: y } : prev));
  }, []);

  const endRightDragSelect = useCallback((event: MouseEvent) => {
    if (!rightSelectingRef.current) return;

    // MouseEvent.button is meaningful on mouseup.
    if ((event as any).button !== 2) return;

    event.preventDefault();
    rightSelectingRef.current = false;

    const box = rightSelectBox;
    setRightSelectBox(null);

    const rf = reactFlowInstance;
    if (!rf || !box) return;

    const sx = Math.min(box.startX, box.endX);
    const sy = Math.min(box.startY, box.endY);
    const ex = Math.max(box.startX, box.endX);
    const ey = Math.max(box.startY, box.endY);

    const p1 = rf.project({ x: sx, y: sy });
    const p2 = rf.project({ x: ex, y: ey });

    const left = Math.min(p1.x, p2.x);
    const top = Math.min(p1.y, p2.y);
    const right = Math.max(p1.x, p2.x);
    const bottom = Math.max(p1.y, p2.y);

    const runtimeNodes: any[] = (typeof rf.getNodes === 'function' ? rf.getNodes() : nodesRef.current) as any[];

    const selectedIds = new Set<string>();
    for (const n of runtimeNodes) {
      const nx = Number(n.positionAbsolute?.x ?? n.position?.x ?? 0);
      const ny = Number(n.positionAbsolute?.y ?? n.position?.y ?? 0);
      const w = Number(n.width ?? 180);
      const h = Number(n.height ?? 60);

      // Partial intersection
      const intersects = !(nx + w < left || nx > right || ny + h < top || ny > bottom);
      if (intersects) selectedIds.add(String(n.id));
    }

    setNodes((nds) =>
      nds.map((n) => ({
        ...n,
        selected: selectedIds.has(String(n.id)),
      }))
    );
  }, [nodesRef, reactFlowInstance, rightSelectBox, setNodes]);

  // Global listeners so selection keeps tracking even if mouse leaves the canvas.
  useEffect(() => {
    const onMove = (e: MouseEvent) => updateRightDragSelect(e);
    const onUp = (e: MouseEvent) => endRightDragSelect(e);

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [updateRightDragSelect, endRightDragSelect]);

  const onNodeContextMenu = useCallback(
    (event: React.MouseEvent, node: Node) => {
      if (!isOwner) return;
      event.preventDefault();
      setMenu({ id: node.id, type: 'node', x: event.clientX, y: event.clientY });
      setTimeout(() => {
        if (contextMenuTriggerRef.current) {
          contextMenuTriggerRef.current.dispatchEvent(new MouseEvent('contextmenu', { bubbles: true, cancelable: true, clientX: event.clientX, clientY: event.clientY, button: 2 }));
        }
      }, 0);
    },
    [isOwner],
  );

  const onEdgeContextMenu = useCallback(
    (event: React.MouseEvent, edge: Edge) => {
      if (!isOwner) return;
      event.preventDefault();
      setMenu({ id: edge.id, type: 'edge', x: event.clientX, y: event.clientY });
      setTimeout(() => {
        if (contextMenuTriggerRef.current) {
          contextMenuTriggerRef.current.dispatchEvent(new MouseEvent('contextmenu', { bubbles: true, cancelable: true, clientX: event.clientX, clientY: event.clientY, button: 2 }));
        }
      }, 0);
    },
    [isOwner],
  );

  const onDeleteSelected = useCallback(() => {
    if (menu && isOwner) {
      if (menu.type === 'node') {
        setNodes((nds) => nds.filter((n) => n.id !== menu.id));
        setEdges((eds) => eds.filter((e) => e.source !== menu.id && e.target !== menu.id));
      } else if (menu.type === 'edge') {
        setEdges((eds) => eds.filter((e) => e.id !== menu.id));
      }
      setMenu(null);
      setIsDirty(true);
    }
  }, [menu, setNodes, setEdges, isOwner]);

  const handleNodeDataChange = (field: string, value: any) => {
    if (!selectedNodeId || !isOwner) return;
    setNodes((nds) => nds.map((node) => node.id === selectedNodeId ? { ...node, data: { ...node.data, [field]: value } } : node));
    setIsDirty(true);
  };

  const handleBlueprintMetaChange = (field: 'name' | 'description' | 'is_private', value: any) => {
    if (!isOwner) return;
    setBlueprint(bp => bp ? { ...bp, [field]: value } : null);
    setIsDirty(true);
  };

  const handleShareWithOrganization = async (organizationId: string | null) => {
    if (!blueprintId || !isOwner) return;

    const { error } = await (supabase as any)
      .from('blueprints')
      .update({ organization_id: organizationId })
      .eq('id', blueprintId);

    if (error) {
      toast({
        title: 'Error updating sharing settings',
        description: error.message,
        variant: 'destructive',
      });
    } else {
      setBlueprint(bp => bp ? ({ ...(bp as any), organization_id: organizationId } as any) : null);
      toast({
        title: 'Sharing settings updated',
        description: organizationId
          ? 'Blueprint is now shared with the organization.'
          : 'Blueprint is no longer shared with an organization.',
      });
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!user || !blueprintId || !selectedNodeId || !isOwner) return;
    setIsUploading(true);
    const filePath = `${user.id}/${blueprintId}/${selectedNodeId}/${file.name}`;
    
    const { error } = await supabase.storage
      .from('blueprint_files')
      .upload(filePath, file, { upsert: true });

    setIsUploading(false);
    if (error) {
      toast({ title: "Upload failed", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Upload successful" });
      handleNodeDataChange("file", { path: filePath, name: file.name, type: file.type });
    }
  };

  const onConnect: OnConnect = useCallback((params: Edge | Connection) => {
    if (!isOwner) return;
    setEdges((eds) => addEdge({ ...params, type: "smoothstep", animated: true, style: { strokeWidth: 2 } }, eds));
    setIsDirty(true);
  }, [setEdges, isOwner]);

  const isUniqueViolation = (err: any) => {
    return err?.code === '23505' || String(err?.message || '').includes('unique_user_blueprint_name');
  };

  const isUntitledBlueprintName = (name?: string | null) => {
    const n = String(name || '').trim();
    if (!n) return true;
    return /^untitled blueprint(\s*\(\d+\))?$/i.test(n);
  };

  const suggestBlueprintNameFromTask = (task: string) => {
    const raw = String(task || '').trim();
    if (!raw) return 'Untitled Blueprint';

    // Use first line / sentence and remove a few common lead-ins.
    const firstLine = raw.split(/\n+/)[0] || raw;
    const firstSentence = firstLine.split(/(?<=[.!?])\s+/)[0] || firstLine;

    let s = firstSentence
      .replace(/^\s*"|"\s*$/g, '')
      .replace(/^(we\s+are\s+going\s+to|we\s+will|i\s+want\s+to|let's|lets|please|help\s+me\s+to)\s+/i, '')
      .replace(/\s+/g, ' ')
      .trim();

    // Light title-casing: capitalize first letter.
    if (s) s = s.charAt(0).toUpperCase() + s.slice(1);

    // Keep it short.
    const maxLen = 52;
    if (s.length > maxLen) s = s.slice(0, maxLen - 1).trimEnd() + '…';

    return s || 'Untitled Blueprint';
  };

  const getDescriptionFromNodes = (nextNodes: Node[]) => {
    const firstUserInputNode = nextNodes.find((node) => node.type === 'user-input');
    const desc = String((firstUserInputNode as any)?.data?.content || '').trim();
    return desc || 'No description provided.';
  };

  const persistBlueprint = async (opts?: {
    nextNodes?: Node[];
    nextEdges?: Edge[];
    nameOverride?: string;
    descriptionOverride?: string;
  }): Promise<boolean> => {
    if (!blueprintId || !blueprint || !isOwner) return false;

    const nextNodes = opts?.nextNodes ?? nodes;
    const nextEdges = opts?.nextEdges ?? edges;

    const desiredName = String(opts?.nameOverride ?? blueprint.name ?? '').trim();
    const desiredDescription = String(opts?.descriptionOverride ?? getDescriptionFromNodes(nextNodes)).trim();

    setIsSaving(true);
    try {
      // Attempt to update with a unique name if needed.
      const baseName = desiredName || 'Untitled Blueprint';
      for (let i = 0; i < 20; i++) {
        const candidateName = i === 0 ? baseName : `${baseName} (${i + 1})`;

        const { error } = await (supabase as any)
          .from('blueprints')
          .update({
            name: candidateName,
            description: desiredDescription,
            is_private: blueprint.is_private,
            data: { nodes: nextNodes, edges: nextEdges },
          } as any)
          .eq('id', blueprintId);

        if (!error) {
          setBlueprint((bp) =>
            bp
              ? ({
                  ...(bp as any),
                  name: candidateName,
                  description: desiredDescription,
                  data: { nodes: nextNodes, edges: nextEdges },
                } as any)
              : null
          );

          setIsDirty(false);
          return true;
        }

        if (!isUniqueViolation(error)) throw error;
      }

      throw new Error('Could not create a unique blueprint name. Please rename an existing blueprint and try again.');
    } catch (error: any) {
      toast({ title: 'Error saving blueprint', description: error.message, variant: 'destructive' });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateBlueprint = async (): Promise<boolean> => {
    return persistBlueprint();
  };

  const ensureAutoSavedForRun = async (task: string) => {
    if (!blueprint || !isOwner) return;

    const suggested = isUntitledBlueprintName(blueprint.name) ? suggestBlueprintNameFromTask(task) : String(blueprint.name);

    const shouldSave = isDirty || suggested !== blueprint.name;
    if (!shouldSave) return;

    await persistBlueprint({ nameOverride: suggested });
  };

  const handleCloneAndEdit = async () => {
    if (!user || !blueprint) {
      toast({ title: 'Authentication Required', description: 'You must be logged in to clone a blueprint.', variant: 'destructive' });
      return;
    }

    const isUniqueViolation = (err: any) => {
      return err?.code === '23505' || String(err?.message || '').includes('unique_user_blueprint_name');
    };

    try {
      for (let i = 0; i < 20; i++) {
        const name = i === 0 ? `Clone of ${blueprint.name}` : `Clone of ${blueprint.name} (${i + 1})`;

        const { data: newBlueprint, error } = await (supabase as any)
          .from('blueprints')
          .insert({
            user_id: user.id,
            name,
            description: blueprint.description,
            data: { nodes, edges },
            is_private: true
          } as any)
          .select('id')
          .single();

        if (!error && newBlueprint) {
          toast({ title: 'Blueprint Cloned!', description: embeddedInMapleOS ? 'Opening your new blueprint...' : 'Opening AI Blueprints...' });
          if (embeddedInMapleOS) {
            onOpenBlueprint?.(newBlueprint.id);
          } else {
            navigate(`/desktop/start?open=ai-blueprints&filePath=${newBlueprint.id}`);
          }
          return;
        }

        if (!isUniqueViolation(error)) {
          throw error;
        }
      }

      throw new Error('Could not create a unique clone name. Please rename an existing blueprint and try again.');
    } catch (error: any) {
      toast({ title: 'Error cloning blueprint', description: error.message, variant: 'destructive' });
    }
  };

  const closeEmbeddedOrNavigateHome = () => {
    if (embeddedInMapleOS) {
      onRequestClose?.();
      return;
    }

    navigate('/desktop/start?open=ai-blueprints');
  };

  const handleBackClick = () => {
    if (isDirty && isOwner) {
      setShowLeaveConfirmDialog(true);
    } else {
      closeEmbeddedOrNavigateHome();
    }
  };

  const handleRunBlueprint = () => {
    // If a run is paused by the user (Pause button), treat header button as Resume.
    if (!isExecuting && activeRunIdRef.current && (executionResult as any)?.awaiting?.type === 'manual-pause') {
      void resumeFromDecision('');
      return;
    }

    if (!hasPathFromStartToEnd(nodes, edges)) {
      toast({ title: "Incomplete Blueprint", description: "You need to connect the start and end nodes to complete the blueprint.", variant: "destructive" });
      return;
    }

    // If a run is currently executing, treat header button as Pause.
    if (isExecuting && activeRunIdRef.current) {
      requestPause();
      return;
    }

    // Starting a new run via the dialog should create a new run record.
    setActiveRunId(null);
    activeRunIdRef.current = null;

    // Reset blossoms for the new run.
    resetBlossoms();
    setAutoBlossomEnabled(false);

    const firstUserInputNode = nodes.find(node => node.type === 'user-input');
    if (firstUserInputNode && firstUserInputNode.data.content) {
      setRunInitialInput(firstUserInputNode.data.content);
    } else {
      setRunInitialInput(localStorage.getItem(`blueprint_input_${blueprintId}`) || '');
    }
    setExecutionResult(null);
    resetRunControls();
    setRunDialogOpen(true);
  };

  const applyPatchAndContinue = async (ctx: ExecutionContext, patch: any) => {
    const parsed = patch;
    const applied = applyBlueprintPatch({ nodes: nodesRef.current, edges: edgesRef.current, patch: parsed });

    // Apply to canvas
    setNodes(applied.nodes);
    setEdges(applied.edges);
    setIsDirty(true);

    // Persist blueprint changes immediately so discoveries are saved as they blossom.
    await persistBlueprint({ nextNodes: applied.nodes, nextEdges: applied.edges });

    // Persist run snapshot so playback can show the blossoming graph over time.
    await persistRunProgress(ctx, { nodes: applied.nodes, edges: applied.edges });

    // Continue execution from requested nodes (or from newly added nodes)
    const continueFrom = Array.isArray(parsed?.continueFrom) && parsed.continueFrom.length
      ? parsed.continueFrom
      : applied.addedNodeIds;

    if (!continueFrom.length) return ctx;

    const next = await continueBlueprintFrom(
      applied.nodes,
      applied.edges,
      ctx,
      continueFrom,
      models,
      mcpServices,
      userApiKeys
    );

    return next;
  };

  const runAutoEvolveLoop = async (initial: ExecutionContext) => {
    if (!initial?.lastAgenticNodeId) return initial;

    let ctx = cloneContext(initial);
    autoEvolveStopRef.current = false;

    // Let it run until user stops, or it hits a decision gate.
    while (!autoEvolveStopRef.current) {
      if (runControlRef.current.stop) {
        ctx.history.push({
          nodeId: ctx.lastAgenticNodeId,
          timestamp: new Date().toISOString(),
          role: 'system',
          lane: 'left',
          message: 'Stopped by user.',
        } as any);
        (ctx as any).runStatus = 'stopped';
        break;
      }

      if (runControlRef.current.pause) {
        ctx.history.push({
          nodeId: ctx.lastAgenticNodeId,
          timestamp: new Date().toISOString(),
          role: 'system',
          lane: 'left',
          message: 'Paused by user.',
        } as any);
        (ctx as any).awaiting = { type: 'manual-pause', nodeId: ctx.lastAgenticNodeId, resumeMode: 'auto-evolve' };
        (ctx as any).runStatus = 'paused';
        break;
      }

      if (ctx.awaiting?.type === 'user-decision') break;
      if (ctx.error) break;

      // Ask agents to continue a bit to generate the next idea
      ctx = await continueAgenticFlow(ctx.lastAgenticNodeId, ctx, 'Continue. Propose the next concrete step.', 1, models);
      setExecutionResult(ctx);
      await persistRunProgress(ctx);

      // Propose a patch based on the current goal and output.
      const patch = await proposeBlueprintPatch({
        goal: String(ctx.variables.userInput || ''),
        anchorNodeId: ctx.lastAgenticNodeId,
        nodes: nodesRef.current,
        edges: edgesRef.current,
        context: ctx,
        appModels: models,
      });

      if (!patch || (!patch.addNodes?.length && !patch.addEdges?.length)) {
        // no changes proposed; stop auto mode
        break;
      }

      // Add a note to execution history
      ctx.history.push({
        nodeId: ctx.lastAgenticNodeId,
        timestamp: new Date().toISOString(),
        role: 'system',
        lane: 'left',
        message: `Auto-evolve: applying patch: ${patch.summary || '(no summary)'}`,
      } as any);

      ctx = await applyPatchAndContinue(ctx, patch);
      setExecutionResult(ctx);
      await persistRunProgress(ctx);

      // tiny pause so UI has time to paint
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 250));
    }

    return ctx;
  };

  // Prefer function declaration (hoisted) so executeFlow can use it for live updates.
  function cloneContext(ctx: ExecutionContext) {
    // Prefer structuredClone when available; otherwise JSON clone.
    try {
      // @ts-ignore
      return typeof structuredClone === 'function' ? structuredClone(ctx) : JSON.parse(JSON.stringify(ctx));
    } catch {
      return JSON.parse(JSON.stringify(ctx));
    }
  }

  const executeFlow = async (overrideInput?: string) => {
    const input = (overrideInput ?? runInitialInput).trim();
    if (!input) return;
    localStorage.setItem(`blueprint_input_${blueprintId}`, input);

    // Auto-save before running. If it's still untitled, derive a short name from the task.
    await ensureAutoSavedForRun(input);

    setCompletedRunRecord(null);

    // Start a new run record for playback
    const runId = await ensureRun(input);

    if (!runId) return;

    setIsExecuting(true);
    // Seed an initial context immediately so the header switches to Pause/Stop right away.
    setExecutionResult({ variables: { userInput: input, lastOutput: input }, history: [], runStatus: 'running' } as any);

    setDecisionValue('');

    setFollowupText('');
    setFollowupRounds(1);
    resetRunControls();

    // Live execution streaming: throttle UI updates to animation frames.
    let raf: number | null = null;
    let latestCtx: ExecutionContext | null = null;
    const onContext = (ctx: ExecutionContext) => {
      latestCtx = ctx;
      if (raf != null) return;
      raf = window.requestAnimationFrame(() => {
        raf = null;
        if (latestCtx) setExecutionResult(cloneContext(latestCtx));
      });
    };

    try {
      let result = await executeBlueprint(nodesRef.current, edgesRef.current, input, models, mcpServices, userApiKeys, {
        onContext,
        control: {
          shouldPause: () => runControlRef.current.pause,
          shouldStop: () => runControlRef.current.stop,
          pauseMode: 'blueprint',
        },
        skipTrust: blueprintTrustDisabled,
      });

      setExecutionResult(result);
      await persistRunProgress(result);

      const statusFor = (ctx: any) => {
        if (ctx?.runStatus === 'stopped') return 'stopped';
        if (ctx?.error) return 'failed';
        if (ctx?.awaiting) return 'paused';
        return 'completed';
      };

      if (autoEvolveEnabled && !result.awaiting && !result.error && (result as any).runStatus !== 'stopped') {
        result = await runAutoEvolveLoop(result);
        setExecutionResult(result);
        await persistRunProgress(result, { status: statusFor(result) });
      } else {
        await persistRunProgress(result, { status: statusFor(result) });
      }

      // For the post-run view, load the saved run record and replay it (fast + stable).
      const { data } = await sb.from('blueprint_runs').select('*').eq('id', runId).single();
      if (data) {
        setCompletedRunRecord(data);
        setSelectedRun(data);
      }

    } catch (error: any) {

      const failed = { variables: {}, history: [], error: error.message || "An unknown error occurred." } as any;
      setExecutionResult(failed);
      await persistRunProgress(failed, { status: 'failed' });

      const { data } = await sb.from('blueprint_runs').select('*').eq('id', runId).single();
      if (data) {
        setCompletedRunRecord(data);
        setSelectedRun(data);
      }
    } finally {
      setIsExecuting(false);
    }
  };

  const resumeFromDecision = async (value: string) => {
    if (!executionResult?.awaiting) return;

    // Manual pause resume (Pause button) resumes without user input.
    if ((executionResult.awaiting as any).type === 'manual-pause') {
      setIsExecuting(true);
      resetRunControls();
      try {
        const mode = (executionResult.awaiting as any).resumeMode;

        if (mode === 'auto-evolve') {
          let ctx = cloneContext(executionResult);
          (ctx as any).awaiting = undefined;
          (ctx as any).runStatus = 'running';
          ctx = await runAutoEvolveLoop(ctx);
          setExecutionResult(ctx);
          await persistRunProgress(ctx, { status: (ctx as any).runStatus === 'stopped' ? 'stopped' : ctx.error ? 'failed' : ctx.awaiting ? 'paused' : 'completed' });
          return;
        }

        const ctx = cloneContext(executionResult);
        const pausedNodeId = (executionResult.awaiting as any).nodeId;
        const next = await resumeBlueprint(
          nodesRef.current,
          edgesRef.current,
          ctx,
          { nodeId: pausedNodeId, value: '' },
          models,
          mcpServices,
          userApiKeys,
          {
            onContext: (c) => setExecutionResult(cloneContext(c)),
            control: {
              shouldPause: () => runControlRef.current.pause,
              shouldStop: () => runControlRef.current.stop,
              pauseMode: 'blueprint',
            },
            skipTrust: blueprintTrustDisabled,
          }
        );
        setExecutionResult(next);
        await persistRunProgress(next);

        if (autoEvolveEnabled && !next.awaiting && !next.error && (next as any).runStatus !== 'stopped') {
          const evolved = await runAutoEvolveLoop(next);
          setExecutionResult(evolved);
          await persistRunProgress(evolved, { status: (evolved as any).runStatus === 'stopped' ? 'stopped' : evolved.error ? 'failed' : evolved.awaiting ? 'paused' : 'completed' });
        }
      } catch (e: any) {
        const failed = { ...(executionResult || { variables: {}, history: [] }), error: e?.message || 'Failed to resume.' } as any;
        setExecutionResult(failed);
        await persistRunProgress(failed, { status: 'failed' });
      } finally {
        setIsExecuting(false);
        resetRunControls();
      }
      return;
    }

    setIsExecuting(true);
    try {
      const ctx = cloneContext(executionResult);
      const next = await resumeBlueprint(
        nodesRef.current,
        edgesRef.current,
        ctx,
        { nodeId: executionResult.awaiting.nodeId, value },
        models,
        mcpServices,
        userApiKeys,
        { skipTrust: blueprintTrustDisabled }
      );
      setExecutionResult(next);
      setDecisionValue('');
      await persistRunProgress(next);

      if (autoEvolveEnabled && !next.awaiting && !next.error) {
        const evolved = await runAutoEvolveLoop(next);
        setExecutionResult(evolved);
        await persistRunProgress(evolved, { status: (evolved as any).runStatus === 'stopped' ? 'stopped' : evolved.error ? 'failed' : evolved.awaiting ? 'paused' : 'completed' });
      }
    } catch (e: any) {
      const failed = { ...(executionResult || { variables: {}, history: [] }), error: e?.message || 'Failed to resume.' } as any;
      setExecutionResult(failed);
      await persistRunProgress(failed, { status: 'failed' });
    } finally {
      setIsExecuting(false);
    }
  };

  const submitFollowup = async () => {
    const msg = followupText.trim();
    if (!msg) return;

    if (!executionResult?.lastAgenticNodeId) {
      setRunInitialInput(msg);
      await executeFlow(msg);
      return;
    }

    setIsExecuting(true);
    try {
      const ctx = cloneContext(executionResult);
      let next = await continueAgenticFlow(ctx.lastAgenticNodeId, ctx, msg, followupRounds, models);
      setExecutionResult(next);
      setFollowupText('');
      setFollowupRounds(1);
      await persistRunProgress(next);

      // If auto-evolve is enabled, let the follow-up blossom into new nodes/edges.
      if (autoEvolveEnabled && !next.awaiting && !next.error) {
        next = await runAutoEvolveLoop(next);
        setExecutionResult(next);
        await persistRunProgress(next, { status: next.error ? 'failed' : next.awaiting ? 'paused' : 'completed' });
      }
    } catch (e: any) {
      const failed = { ...(executionResult || { variables: {}, history: [] }), error: e?.message || 'Failed to continue.' } as any;
      setExecutionResult(failed);
      await persistRunProgress(failed, { status: 'failed' });
    } finally {
      setIsExecuting(false);
    }
  };

  const onNodesChangeCustom = useCallback((changes) => {
    if (!isOwner) return;
    onNodesChange(changes);
    setIsDirty(true);
  }, [onNodesChange, isOwner]);

  const onEdgesChangeCustom = useCallback((changes) => {
    if (!isOwner) return;
    onEdgesState(changes);
    setIsDirty(true);
  }, [onEdgesState, isOwner]);

  const buildBlueprintFromPrompt = async () => {
    const p = aiBuildPrompt.trim().toLowerCase();
    if (!p) {
      toast({ title: 'Prompt required', description: 'Describe what you want the blueprint to do.', variant: 'destructive' });
      return;
    }

    // v1: deterministic templates
    const looksLikeWeather = p.includes('weather') || p.includes('forecast');
    const looksLikeDiscuss = p.includes('discuss') || p.includes('another agent') || p.includes('second agent') || p.includes('with another');

    const looksLikeCancer =
      p.includes('cancer') ||
      p.includes('oncology') ||
      p.includes('tumor') ||
      p.includes('tumour') ||
      p.includes('immunotherapy') ||
      p.includes('chemotherapy');

    const looksLikeAgentic =
      p.includes('agentic flow') ||
      p.includes('two agents') ||
      p.includes('debate') ||
      p.includes('multi-agent') ||
      p.includes('multi agent') ||
      (p.includes('agents') && (p.includes('discuss') || p.includes('brainstorm') || p.includes('research') || p.includes('work on') || p.includes('solve'))) ||
      looksLikeCancer;

    const wantsDecisionGate =
      p.includes('user decision') ||
      p.includes('pause') ||
      p.includes('then asks me') ||
      p.includes('then ask me') ||
      p.includes('asks me') ||
      p.includes('ask me') ||
      p.includes('what direction') ||
      p.includes('what should i do next') ||
      p.includes('choose next');

    if (looksLikeWeather && looksLikeDiscuss) {
      // Existing template: weather → discuss with another agent

      // Layout
      const startX = 40;
      const startY = 180;
      const stepX = 240;

      const nStart: Node = {
        id: 'start',
        type: 'start',
        position: { x: startX, y: startY },
        data: { type: 'start', label: 'Start', description: 'Blueprint begins here', content: '' },
        deletable: false,
        draggable: true,
      };

      const nUser: Node = {
        id: 'user-input-1',
        type: 'user-input',
        position: { x: startX + stepX, y: startY },
        data: { type: 'user-input', label: 'Location', description: 'City name (e.g., Toronto)', content: 'Toronto' },
      };

      // Simple weather JSON without needing lat/lon
      // wttr.in supports city-in-URL and returns JSON with ?format=j1
      const nWeather: Node = {
        id: 'api-call-weather',
        type: 'api-call',
        position: { x: startX + stepX * 2, y: startY },
        data: {
          type: 'api-call',
          label: 'Fetch weather',
          description: 'Get current conditions',
          apiRequest: {
            url: 'https://wttr.in/{{lastOutput}}?format=j1',
            method: 'GET',
            headers: {},
            body: '',
          },
          content: 'https://wttr.in/{{lastOutput}}?format=j1',
        },
      };

      const nAgent1: Node = {
        id: 'ai-response-1',
        type: 'ai-response',
        position: { x: startX + stepX * 3, y: startY - 80 },
        data: {
          type: 'ai-response',
          label: 'Agent 1 analysis',
          description: 'Summarize the weather',
          content: '',
        },
      };

      const nSystem2: Node = {
        id: 'system-prompt-2',
        type: 'system-prompt',
        position: { x: startX + stepX * 3, y: startY + 80 },
        data: {
          type: 'system-prompt',
          label: 'Agent 2 role',
          description: 'Challenge and critique',
          content: 'You are Agent 2. Challenge assumptions and ask clarifying questions.',
        },
      };

      const nAgent2: Node = {
        id: 'ai-response-2',
        type: 'ai-response',
        position: { x: startX + stepX * 4, y: startY },
        data: {
          type: 'ai-response',
          label: 'Agent 2 response',
          description: 'Respond to Agent 1',
          content: '',
        },
      };

      const nEnd: Node = {
        id: 'end',
        type: 'end',
        position: { x: startX + stepX * 5, y: startY },
        data: { type: 'end', label: 'End', description: 'Blueprint ends here', content: '' },
        deletable: false,
        draggable: true,
      };

      const e = (id: string, source: string, target: string, sourceHandle?: string | null) => ({
        id,
        source,
        target,
        sourceHandle,
        animated: true,
        style: { strokeWidth: 2 },
      });

      const newNodes: Node[] = [nStart, nUser, nWeather, nAgent1, nSystem2, nAgent2, nEnd];
      const newEdges: Edge[] = [
        e('e1', 'start', 'user-input-1'),
        e('e2', 'user-input-1', 'api-call-weather'),
        e('e3', 'api-call-weather', 'ai-response-1'),
        e('e4', 'ai-response-1', 'system-prompt-2'),
        e('e5', 'system-prompt-2', 'ai-response-2'),
        e('e6', 'ai-response-2', 'end'),
      ];

      setNodes(newNodes);
      setEdges(newEdges);
      setSelectedNodeId(null);
      setIsDirty(true);
      setAiBuildOpen(false);

      toast({ title: 'Blueprint built', description: 'Weather → Agent discussion flow created. Click Run to watch it execute.' });
      return;
    }

    if (looksLikeAgentic) {
      // Template: user input → (optional decision) → agentic flow → (optional decision) → end
      const startX = 40;
      const startY = 180;
      const stepX = 240;

      const nStart: Node = {
        id: 'start',
        type: 'start',
        position: { x: startX, y: startY },
        data: { type: 'start', label: 'Start', description: 'Blueprint begins here', content: '' },
        deletable: false,
        draggable: true,
      };

      const isCancerFlow = looksLikeCancer;

      const inputLabel = isCancerFlow
        ? 'Cancer focus'
        : p.includes('startup')
          ? 'Startup idea'
          : p.includes('topic')
            ? 'Topic'
            : 'Prompt';

      const inputDesc = isCancerFlow
        ? 'Specify cancer type, patient context, and goal (research directions; no "cure" claims)'
        : p.includes('startup')
          ? 'Describe your startup idea'
          : 'What should the agents discuss?';

      const inputDefault = isCancerFlow
        ? 'Focus: pancreatic cancer. Goal: identify 5–10 promising, evidence-backed treatment directions (modalities, biomarkers, combination strategies), key uncertainties, and suggested next-step experiments; include citations where possible.'
        : p.includes('startup')
          ? 'A B2B tool that helps teams summarize customer calls into action items'
          : 'Should I use Postgres or MongoDB for a multi-tenant SaaS?';

      const nUser: Node = {
        id: 'user-input-1',
        type: 'user-input',
        position: { x: startX + stepX, y: startY },
        data: { type: 'user-input', label: inputLabel, description: inputDesc, content: inputDefault },
      };

      const nModel: Node = {
        id: 'model-selector-1',
        type: 'model-selector',
        position: { x: startX + stepX * 2, y: startY },
        data: { type: 'model-selector', label: 'Model', description: 'Choose a model (optional)', content: '' },
      };

      const agents = isCancerFlow
        ? [
            {
              id: 'agent1',
              name: 'Literature Scout',
              persona: 'You are the Literature Scout. Summarize recent review-level evidence and notable clinical trial directions; be explicit about uncertainty and avoid claiming a cure.',
              modelId: '',
            },
            {
              id: 'agent2',
              name: 'Mechanisms Analyst',
              persona: 'You are the Mechanisms Analyst. Map hallmarks of cancer, targets, resistance mechanisms, and biomarkers relevant to the stated cancer focus.',
              modelId: '',
            },
            {
              id: 'agent3',
              name: 'Therapeutics Mapper',
              persona: 'You are the Therapeutics Mapper. Propose modalities and combinations (small molecules, biologics, cell therapy, vaccines, radiotherapy combos), plus practical constraints (toxicity, delivery).',
              modelId: '',
            },
            {
              id: 'agent4',
              name: 'Trial/Regulatory Analyst',
              persona: 'You are the Trial/Regulatory Analyst. Identify plausible trial designs, endpoints, stratification, and safety considerations; highlight what would be needed to move a hypothesis forward.',
              modelId: '',
            },
          ]
        : [
            {
              id: 'agent1',
              name: 'Agent 1',
              persona: 'You are Agent 1. Analyze and propose an initial position.',
              modelId: '',
            },
            {
              id: 'agent2',
              name: 'Agent 2',
              persona: 'You are Agent 2. Challenge assumptions and ask clarifying questions.',
              modelId: '',
            },
            {
              id: 'agent3',
              name: 'Agent 3',
              persona: 'You are Agent 3. Provide alternative options, edge cases, and practical constraints.',
              modelId: '',
            },
          ];

      const nAgentic: Node = {
        id: 'agentic-flow-1',
        type: 'agentic-flow',
        position: { x: startX + stepX * 3, y: startY },
        data: {
          type: 'agentic-flow',
          label: isCancerFlow ? 'Oncology Research Flow' : 'Agentic Flow',
          description: isCancerFlow ? 'Multi-agent research synthesis' : 'Multi-agent round-robin',
          agents,
          agent1Persona: isCancerFlow
            ? 'You are the Literature Scout. Summarize recent review-level evidence and notable clinical trial directions; be explicit about uncertainty and avoid claiming a cure.'
            : 'You are Agent 1. Analyze and propose an initial position.',
          agent2Persona: isCancerFlow
            ? 'You are the Mechanisms Analyst. Map hallmarks of cancer, targets, resistance mechanisms, and biomarkers relevant to the stated cancer focus.'
            : 'You are Agent 2. Challenge assumptions and ask clarifying questions.',
          rounds: isCancerFlow ? 4 : 3,
          modelSource: 'inherit',
          modelA: '',
          modelB: '',
          retrieval: {
            useLocal: true,
            kbId: '',
            topK: 5,
            threshold: 0.75,
            showSources: true,
            autoIndex: false,
          },
          returnMode: 'summary',
        },
      };

      const nDecision: Node = {
        id: 'user-decision-1',
        type: 'user-decision',
        position: { x: startX + stepX * 4, y: startY },
        data: {
          type: 'user-decision',
          label: 'Decision',
          description: 'Pause and collect input',
          prompt: isCancerFlow ? 'Which cancer type / patient context should we focus on next?' : 'What direction should we pursue next?',
          variable: 'decisionInput',
          allowEmpty: false,
        },
      };

      const nEnd: Node = {
        id: 'end',
        type: 'end',
        position: { x: startX + stepX * (wantsDecisionGate ? 6 : 5), y: startY },
        data: { type: 'end', label: 'End', description: 'Blueprint ends here', content: '' },
        deletable: false,
        draggable: true,
      };

      const e = (id: string, source: string, target: string, sourceHandle?: string | null) => ({
        id,
        source,
        target,
        sourceHandle,
        animated: true,
        style: { strokeWidth: 2 },
      });

      const newNodes: Node[] = [nStart, nUser, nModel, nAgentic];
      const newEdges: Edge[] = [e('e1', 'start', 'user-input-1'), e('e2', 'user-input-1', 'model-selector-1'), e('e3', 'model-selector-1', 'agentic-flow-1')];

      if (wantsDecisionGate) {
        newNodes.push(nDecision);
        newNodes.push(nEnd);
        newEdges.push(e('e4', 'agentic-flow-1', 'user-decision-1'));
        newEdges.push(e('e5', 'user-decision-1', 'end'));
      } else {
        newNodes.push(nEnd);
        newEdges.push(e('e4', 'agentic-flow-1', 'end'));
      }

      setNodes(newNodes);
      setEdges(newEdges);
      setSelectedNodeId(null);
      setIsDirty(true);
      setAiBuildOpen(false);

      toast({
        title: 'Blueprint built',
        description: isCancerFlow
          ? 'Oncology research flow created (evidence + uncertainty focused). Click Run to execute.'
          : wantsDecisionGate
            ? 'User Input → Agentic Flow → User Decision flow created. Click Run to start, then resume when prompted.'
            : 'User Input → Agentic Flow flow created. Click Run to watch it execute.',
      });
      return;
    }

    toast({
      title: 'Not supported yet',
      description: 'AI Blueprint Builder (v1) currently supports: (1) weather → discuss with another agent, (2) agentic flow debates (with optional user decision).',
      variant: 'destructive',
    });
  };

  const displayNodes = useMemo(() => {
    if (!activeTheaterNodeId) return nodes;
    return (nodes as any[]).map((n) => ({
      ...n,
      data: { ...(n.data || {}), __runtimeActive: n.id === activeTheaterNodeId },
    }));
  }, [nodes, activeTheaterNodeId]);

  useEffect(() => {
    if (!runDialogOpen && !runsOpen) {
      setActiveTheaterNodeId(null);
    }
  }, [runDialogOpen, runsOpen]);

  if (loading) {
    return <div className={cn("flex items-center justify-center", embeddedInMapleOS ? "h-full min-h-0" : "h-screen")}><Skeleton className="w-full h-full" /></div>;
  }

  return (
    <div className={cn("flex flex-col bg-background", embeddedInMapleOS ? "h-full min-h-0" : "h-screen")}>
      <header className="flex items-center justify-between p-2 border-b shrink-0">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={handleBackClick}>
            <ChevronLeft className="h-5 w-5" />
          </Button>
          {isEditingTitle && isOwner ? (
            <Input
              value={blueprint?.name || ''}
              onChange={e => handleBlueprintMetaChange('name', e.target.value)}
              onBlur={async () => {
                setIsEditingTitle(false);
                if (isDirty) await handleUpdateBlueprint();
              }}
              onKeyDown={async (e) => {
                if (e.key === 'Enter') {
                  setIsEditingTitle(false);
                  if (isDirty) await handleUpdateBlueprint();
                }
                if (e.key === 'Escape') setIsEditingTitle(false);
              }}
              className="text-lg font-semibold w-64"
              autoFocus
            />
          ) : (
            <h1
              className={cn("text-lg font-semibold px-3 py-2 rounded-md w-64 truncate", isOwner && "hover:bg-muted cursor-pointer")}
              onClick={() => isOwner && setIsEditingTitle(true)}
              title={isOwner ? "Click to edit" : blueprint?.name}
            >
              {blueprint?.name || 'Untitled Blueprint'}
            </h1>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isOwner ? (
            <>
              <div className="flex items-center space-x-2">
                <Switch id="privacy-toggle" checked={!blueprint?.is_private} onCheckedChange={checked => handleBlueprintMetaChange('is_private', !checked)} />
                <Label htmlFor="privacy-toggle" className="flex items-center gap-1.5 text-sm">
                  {blueprint?.is_private ? <Lock className="h-4 w-4" /> : <Globe className="h-4 w-4" />}
                  {blueprint?.is_private ? 'Private' : 'Public'}
                </Label>
              </div>
              <Button variant="outline" size="sm" onClick={() => { setRunsOpen(true); loadRuns(); }}>
                Runs
              </Button>
              <Button variant="outline" size="sm" onClick={() => setAiBuildOpen(true)}>
                <Wand2 className="h-4 w-4 mr-2" /> AI Build
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowShareDialog(true)}><Share2 className="h-4 w-4 mr-2" /> Share</Button>
              <Button variant="default" size="sm" onClick={handleUpdateBlueprint} disabled={isSaving || !isDirty}>
                {isSaving ? <><Cpu className="h-4 w-4 mr-2 animate-spin" />Saving...</> : <><Save className="h-4 w-4 mr-2" /> Save</>}
              </Button>
            </>
          ) : (
            <Button variant="outline" size="sm" onClick={handleCloneAndEdit}>
              <Copy className="h-4 w-4 mr-2" /> Clone to Edit
            </Button>
          )}
          <div className="flex items-center gap-2">
            <Button
              variant={runIsActive ? 'secondary' : 'default'}

              size="sm"
              className="gap-2"
              onClick={handleRunBlueprint}
            >
              {runIsActive && !runIsPaused ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              {runIsPaused ? 'Resume' : runIsActive ? 'Pause' : 'Run'}

            </Button>
            {runIsActive ? (

              <Button
                variant="destructive"
                size="sm"
                className="gap-2"
                onClick={stopAndClearActiveRun}
              >
                <Square className="h-4 w-4" /> Stop
              </Button>
            ) : null}
          </div>

        </div>
      </header>
      <div className="flex-1 w-full overflow-hidden flex">
        <aside className="w-72 border-r flex-shrink-0 h-full overflow-y-auto">
          <NodePalette onDragStart={onNodeDragStart} embeddedInMapleOS={embeddedInMapleOS} />
        </aside>
        <main className="flex-1 relative h-full min-w-0 overflow-hidden">
          <div
            ref={reactFlowWrapper}
            className={cn("flex-1 relative transition-all h-full min-w-0 overflow-hidden", isPanelOpen ? "pr-[370px]" : "")}
            onContextMenu={(e) => e.preventDefault()}
            onMouseDown={beginRightDragSelect}
          >
            {/* Custom right-drag selection rectangle overlay */}
            {rightSelectBox && (
              <div
                className="react-flow__selection absolute z-20 pointer-events-none"
                style={{
                  left: Math.min(rightSelectBox.startX, rightSelectBox.endX),
                  top: Math.min(rightSelectBox.startY, rightSelectBox.endY),
                  width: Math.abs(rightSelectBox.endX - rightSelectBox.startX),
                  height: Math.abs(rightSelectBox.endY - rightSelectBox.startY),
                }}
              />
            )}

            <ReactFlowProvider>
              <ReactFlow
                nodes={displayNodes as any}
                edges={edges}
                onNodesChange={onNodesChangeCustom}
                onEdgesChange={onEdgesChangeCustom}
                onConnect={onConnect}
                onInit={setReactFlowInstance}
                fitView
                nodeTypes={nodeTypes}
                onDrop={onDrop}
                onDragOver={onDragOver}
                proOptions={{ hideAttribution: true }}
                className="bg-gradient-to-br from-background to-muted/40"
                onNodeClick={onNodeClick}
                onPaneClick={onPaneClick}
                onPaneContextMenu={onPaneContextMenu}
                onNodeContextMenu={onNodeContextMenu}
                onEdgeContextMenu={onEdgeContextMenu}
                // Flip actions: left-drag pans.
                panOnDrag={[0]}
              >
                <Background variant={BackgroundVariant.Lines} gap={32} color="hsl(var(--border))" />
                <MiniMap
                  nodeColor={(n) => getNodeDef(n.type)?.accentColor || "#888"}
                  pannable
                  zoomable
                />
                <Controls showInteractive={false} />
              </ReactFlow>
            </ReactFlowProvider>
          </div>
          {selectedNode && (
            <aside className="w-[360px] border-l bg-card h-full flex flex-col absolute right-0 top-0 z-10 shadow-lg">
              <Card className="h-full rounded-none border-0 flex flex-col min-h-0">
                <CardHeader className="border-b shrink-0"><CardTitle>Node Properties</CardTitle></CardHeader>
                <CardContent className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-4 py-6">
                  <div><Label>Node Type</Label><Input value={selectedNode.data.type} readOnly className="bg-muted" /></div>
                  <div><Label>Label</Label><Input value={selectedNode.data.label || ""} onChange={(e) => handleNodeDataChange("label", e.target.value)} placeholder="Node label" disabled={!isOwner || selectedNode.id === "start" || selectedNode.id === "end"} /></div>
                  <div><Label>Description</Label><Input value={selectedNode.data.description || ""} onChange={(e) => handleNodeDataChange("description", e.target.value)} placeholder="Short description" disabled={!isOwner || selectedNode.id === "start" || selectedNode.id === "end"} /></div>
                  
                  {selectedNode.data.type === 'configuration' ? (
                    <div className="space-y-6 pt-2">
                      <div>
                        <div className="flex justify-between items-center">
                          <Label>Temperature</Label>
                          <span className="text-sm font-medium text-muted-foreground">{selectedNode.data.temperature ?? 1}</span>
                        </div>
                        <Slider
                          value={[selectedNode.data.temperature ?? 1]}
                          onValueChange={([value]) => handleNodeDataChange("temperature", value)}
                          max={2}
                          step={0.1}
                          disabled={!isOwner}
                        />
                        <p className="text-xs text-muted-foreground mt-1">Controls randomness. Higher is more creative. (Default: 1)</p>
                      </div>
                      <div>
                        <Label>Max Tokens</Label>
                        <Input
                          type="number"
                          value={selectedNode.data.max_tokens || ""}
                          onChange={(e) => handleNodeDataChange("max_tokens", parseInt(e.target.value, 10) || undefined)}
                          placeholder="Default (e.g., 1500)"
                          disabled={!isOwner}
                        />
                        <p className="text-xs text-muted-foreground mt-1">Max completion length.</p>
                      </div>
                      <div>
                        <div className="flex justify-between items-center">
                          <Label>Top P</Label>
                          <span className="text-sm font-medium text-muted-foreground">{selectedNode.data.top_p ?? 1}</span>
                        </div>
                        <Slider
                          value={[selectedNode.data.top_p ?? 1]}
                          onValueChange={([value]) => handleNodeDataChange("top_p", value)}
                          max={1}
                          step={0.05}
                          disabled={!isOwner}
                        />
                        <p className="text-xs text-muted-foreground mt-1">Nucleus sampling. (Default: 1)</p>
                      </div>
                    </div>
                  ) : selectedNode.data.type === 'model-selector' ? (
                    <div className="space-y-4">
                      <div>
                        <Label>Selected Model</Label>
                        <ModelSelector
                          models={models}
                          selectedModelId={selectedNode.data.modelId || ''}
                          onModelChange={(modelId) => handleNodeDataChange("modelId", modelId)}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Choose the AI model to be used at this step of the blueprint.
                      </p>
                    </div>
                  ) : selectedNode.data.type === 'conditional' ? (
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        This node evaluates a condition and directs the flow to the 'True' or 'False' path. The condition is checked against the 'lastOutput' from the previous node.
                      </p>
                      <div>
                        <Label>Condition</Label>
                        <div className="flex items-center gap-2">
                          <Input value="{{lastOutput}}" disabled className="w-1/3 bg-muted" />
                          <Select
                            value={selectedNode.data.operator || 'contains'}
                            onValueChange={(value) => handleNodeDataChange("operator", value)}
                            disabled={!isOwner}
                          >
                            <SelectTrigger className="w-1/3">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="contains">contains</SelectItem>
                              <SelectItem value="not-contains">not contains</SelectItem>
                              <SelectItem value="==">== (is equal to)</SelectItem>
                              <SelectItem value="!=">!= (is not equal to)</SelectItem>
                              <SelectItem value=">">&gt; (greater than)</SelectItem>
                              <SelectItem value="<">&lt; (less than)</SelectItem>
                              <SelectItem value=">=">&gt;= (greater/equal)</SelectItem>
                              <SelectItem value="<=">&lt;= (less/equal)</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input
                            value={selectedNode.data.value || ""}
                            onChange={(e) => handleNodeDataChange("value", e.target.value)}
                            placeholder="Value to check"
                            className="w-1/3"
                            disabled={!isOwner}
                          />
                        </div>
                      </div>
                    </div>
                  ) : selectedNode.data.type === 'data' ? (
                    <div className="space-y-4">
                      <div>
                        <Label>Data Source Type</Label>
                        <Select onValueChange={(value) => handleNodeDataChange("sourceType", value)} defaultValue={selectedNode.data.sourceType || 'static-text'} disabled={!isOwner}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select data source" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="static-text">Static Text</SelectItem>
                            <SelectItem value="file-upload">File Upload</SelectItem>
                            <SelectItem value="database">Database Connection</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {(!selectedNode.data.sourceType || selectedNode.data.sourceType === 'static-text') && (
                        <div>
                          <Label>Content</Label>
                          <Textarea value={selectedNode.data.content || ""} onChange={(e) => handleNodeDataChange("content", e.target.value)} placeholder="Node content or prompt" rows={5} disabled={!isOwner} />
                        </div>
                      )}

                      {selectedNode.data.sourceType === 'file-upload' && (
                        <div className="space-y-2">
                          <Label>Upload File</Label>
                          <Input
                            type="file"
                            onChange={(e) => e.target.files && handleFileUpload(e.target.files[0])}
                            disabled={!isOwner || isUploading}
                            accept=".txt,.csv,.xlsx"
                          />
                          {isUploading && <p className="text-sm text-muted-foreground">Uploading...</p>}
                          {selectedNode.data.file && (
                            <div className="text-sm text-muted-foreground">
                              Uploaded: {selectedNode.data.file.name}
                            </div>
                          )}
                        </div>
                      )}

                      {selectedNode.data.sourceType === 'database' && (
                        <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
                           <p className="text-sm text-center text-muted-foreground font-semibold">Database connection is not yet implemented.</p>
                          <div>
                            <Label>Database URL</Label>
                            <Input placeholder="postgresql://user:password@host:port/db" disabled={true} />
                          </div>
                          <div>
                            <Label>SQL Query</Label>
                            <Textarea placeholder="SELECT * FROM users;" rows={3} disabled={true} />
                          </div>
                          {!blueprint?.is_private && (
                            <p className="text-orange-500 text-sm mt-2">
                              ⚠️ Warning: This blueprint is public. Do not expose database credentials.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  
                  ) : selectedNode.data.type === 'knowledge-base' ? (
                    <div className="space-y-4">
                      <div>
                        <Label>Mode</Label>
                        <Select
                          value={String(selectedNode.data.mode || 'select')}
                          onValueChange={(value) => handleNodeDataChange('mode', value)}
                          disabled={!isOwner}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select mode" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="select">Use existing Knowledge Base</SelectItem>
                            <SelectItem value="deep-research">Deep Research (create & fill a new KB)</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-2">
                          Deep Research will web-search and scrape sources, then store them into a new private knowledge base.
                        </p>
                      </div>

                      {String(selectedNode.data.mode || 'select') === 'select' ? (
                        <div>
                          <Label>Knowledge Base</Label>
                          {knowledgeBases.length === 0 ? (
                            <Input value="No Knowledge Bases found" readOnly disabled />
                          ) : (
                            <Select
                              value={
                                knowledgeBases.some(kb => kb.id === String(selectedNode.data.content || ''))
                                  ? String(selectedNode.data.content)
                                  : ""
                              }
                              onValueChange={(value) => handleNodeDataChange("content", value)}
                              disabled={!isOwner}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select a Knowledge Base" />
                              </SelectTrigger>
                              <SelectContent>
                                {knowledgeBases.map(kb => (
                                  <SelectItem key={kb.id} value={kb.id}>
                                    {kb.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          {!blueprint?.is_private && (
                            <p className="text-orange-500 text-sm mt-2">
                              ⚠️ This blueprint is public. Access to private KBs at run time depends on the runner's permissions.
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-4 rounded-lg border bg-muted/20 p-4">
                          <div>
                            <Label>New KB name</Label>
                            <Input
                              value={String(selectedNode.data.deepResearch?.kbName || '')}
                              onChange={(e) => handleNodeDataChange('deepResearch', { ...(selectedNode.data.deepResearch || {}), kbName: e.target.value })}
                              placeholder="e.g. Deep Research — Pancreatic cancer"
                              disabled={!isOwner}
                            />
                          </div>
                          <div>
                            <Label>New KB description</Label>
                            <Input
                              value={String(selectedNode.data.deepResearch?.kbDescription || '')}
                              onChange={(e) => handleNodeDataChange('deepResearch', { ...(selectedNode.data.deepResearch || {}), kbDescription: e.target.value })}
                              placeholder="Optional"
                              disabled={!isOwner}
                            />
                          </div>
                          <div>
                            <Label>Search terms (one per line)</Label>
                            <Textarea
                              value={String(selectedNode.data.deepResearch?.queries || '')}
                              onChange={(e) => handleNodeDataChange('deepResearch', { ...(selectedNode.data.deepResearch || {}), queries: e.target.value })}
                              rows={6}
                              placeholder={
                                "pancreatic cancer KRAS inhibitor clinical trial\n\"CD40 agonist\" pancreatic cancer\npancreatic cancer microbiome immunotherapy"
                              }
                              disabled={!isOwner}
                            />
                            <p className="text-xs text-muted-foreground mt-2">
                              If empty, it will use the blueprint's user input / last output.
                            </p>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label>Results per query</Label>
                              <Input
                                type="number"
                                value={Number(selectedNode.data.deepResearch?.resultsPerQuery ?? 6)}
                                onChange={(e) => handleNodeDataChange('deepResearch', { ...(selectedNode.data.deepResearch || {}), resultsPerQuery: Number(e.target.value || 6) })}
                                min={1}
                                max={10}
                                disabled={!isOwner}
                              />
                            </div>
                            <div>
                              <Label>Scrape top N</Label>
                              <Input
                                type="number"
                                value={Number(selectedNode.data.deepResearch?.scrapeTopN ?? 4)}
                                onChange={(e) => handleNodeDataChange('deepResearch', { ...(selectedNode.data.deepResearch || {}), scrapeTopN: Number(e.target.value || 4) })}
                                min={0}
                                max={10}
                                disabled={!isOwner}
                              />
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Note: this requires being logged in and will create a new private KB under your account.
                          </p>
                        </div>
                      )}
                    </div>
                  ) : selectedNode.data.type === 'mcp' ? (
                    <div className="space-y-4">
                      <div>
                        <Label>MCP Service</Label>
                        {mcpServices.length === 0 ? (
                          <Input value="No MCP services available" readOnly disabled />
                        ) : (
                          <Select
                            value={
                              mcpServices.some(svc => svc.id === String(selectedNode.data.content || ''))
                                ? String(selectedNode.data.content)
                                : ""
                            }
                            onValueChange={(value) => handleNodeDataChange("content", value)}
                            disabled={!isOwner}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select an MCP service" />
                            </SelectTrigger>
                            <SelectContent>
                              {mcpServices.map(service => (
                                <SelectItem key={service.id} value={service.id}>
                                  {service.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {selectedNode.data.content &&
                          getApiKey(String(selectedNode.data.content)) &&
                          !getApiKey(String(selectedNode.data.content)) && (
                            <p className="text-orange-500 text-sm mt-2">
                              ⚠️ No API key set for this service. Add one in{" "}
                              <Link to="/settings/mcp" className="underline">MCP Settings</Link>.
                            </p>
                        )}
                        <p className="text-muted-foreground text-sm mt-2">
                          Note: Public blueprints don't expose your keys; other users must add their own keys to run this node.
                        </p>
                      </div>
                    </div>
                  ) : selectedNode.data.type === 'mapleclaw-gateway' ? (
                    <div className="space-y-4">
                      <div>
                        <Label>Mode</Label>
                        <Select
                          value={String(selectedNode.data.mode || 'send-message')}
                          onValueChange={(value) => handleNodeDataChange('mode', value)}
                          disabled={!isOwner}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select MapleClaw mode" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="send-message">Send message</SelectItem>
                            <SelectItem value="heartbeat-ping">Heartbeat ping</SelectItem>
                            <SelectItem value="list-sessions">List sessions</SelectItem>
                            <SelectItem value="get-transcript">Get transcript</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label>Session key</Label>
                        <Input
                          value={String(selectedNode.data.sessionKey || 'main')}
                          onChange={(event) => handleNodeDataChange('sessionKey', event.target.value)}
                          placeholder="main"
                          disabled={!isOwner}
                        />
                      </div>

                      {['send-message', 'heartbeat-ping'].includes(String(selectedNode.data.mode || 'send-message')) ? (
                        <div>
                          <Label>Payload</Label>
                          <Textarea
                            value={String(selectedNode.data.payload || '')}
                            onChange={(event) => handleNodeDataChange('payload', event.target.value)}
                            rows={5}
                            placeholder="Use {{lastOutput}} or {{userInput}}"
                            disabled={!isOwner}
                          />
                        </div>
                      ) : null}

                      {String(selectedNode.data.mode || 'send-message') === 'send-message' ? (
                        <>
                          <div>
                            <Label>Message runtime</Label>
                            <Select
                              value={String(selectedNode.data.processingRuntime || 'local')}
                              onValueChange={(value) => handleNodeDataChange('processingRuntime', value)}
                              disabled={!isOwner}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select message runtime" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="local">Local MapleClaw</SelectItem>
                                <SelectItem value="pi-agent">pi-agent</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex items-center justify-between gap-3 rounded-md border p-3">
                            <div>
                              <div className="text-sm font-medium">Wait for MapleClaw response</div>
                              <div className="text-xs text-muted-foreground">If disabled, the node returns immediately after the message is queued.</div>
                            </div>
                            <Switch
                              checked={Boolean(selectedNode.data.awaitResponse ?? true)}
                              onCheckedChange={(checked) => handleNodeDataChange('awaitResponse', checked)}
                              disabled={!isOwner}
                            />
                          </div>
                          <div>
                            <Label>{String(selectedNode.data.processingRuntime || 'local') === 'pi-agent' ? 'pi-agent preferred model' : 'Override model'}</Label>
                            <ModelSelector
                              models={models}
                              selectedModelId={String(selectedNode.data.modelId || '')}
                              onModelChange={(modelId) => handleNodeDataChange('modelId', modelId)}
                            />
                          </div>
                          {String(selectedNode.data.processingRuntime || 'local') === 'pi-agent' ? (
                            <div>
                              <Label>pi-agent instructions</Label>
                              <Textarea
                                value={String(selectedNode.data.agentInstruction || '')}
                                onChange={(event) => handleNodeDataChange('agentInstruction', event.target.value)}
                                rows={4}
                                placeholder="Optional guidance for pi-agent in this node"
                                disabled={!isOwner}
                              />
                            </div>
                          ) : null}
                        </>
                      ) : null}

                      {String(selectedNode.data.mode || 'send-message') === 'get-transcript' ? (
                        <div>
                          <Label>Transcript tail length</Label>
                          <Input
                            type="number"
                            min={1}
                            max={200}
                            value={Number(selectedNode.data.tailLines ?? 50)}
                            onChange={(event) => handleNodeDataChange('tailLines', Number(event.target.value || 50))}
                            disabled={!isOwner}
                          />
                        </div>
                      ) : null}

                      <div>
                        <Label>Timeout (ms)</Label>
                        <Input
                          type="number"
                          min={1000}
                          max={120000}
                          value={Number(selectedNode.data.timeoutMs ?? 30000)}
                          onChange={(event) => handleNodeDataChange('timeoutMs', Number(event.target.value || 30000))}
                          disabled={!isOwner}
                        />
                      </div>

                      <p className="text-xs text-muted-foreground">
                        MapleClaw works in both the browser and MapleOS Electron app. Use the local runtime for direct orchestration or switch to pi-agent for MaplePT-backed operator responses.
                      </p>

                    </div>

                  ) : selectedNode.data.type === 'intelligencexp' ? (
                    <div className="space-y-4">
                      <div>
                        <Label>Mode</Label>
                        <Select
                          value={String(selectedNode.data.mode || 'search')}
                          onValueChange={(v) => handleNodeDataChange('mode', v)}
                          disabled={!isOwner}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select mode" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="search">Search</SelectItem>
                            <SelectItem value="read">Read</SelectItem>
                            <SelectItem value="index">Index</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {(selectedNode.data.mode || 'search') === 'search' && (
                        <div className="space-y-4">
                          <div>
                            <Label>Domain</Label>
                            <Select
                              value={String(selectedNode.data.domain || 'files')}
                              onValueChange={(v) => handleNodeDataChange('domain', v)}
                              disabled={!isOwner}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select domain" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="files">Files (local)</SelectItem>
                                <SelectItem value="knowledge-base">Knowledge Base</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <Label>Query Source</Label>
                            <Select
                              value={String(selectedNode.data.querySource || 'user-input')}
                              onValueChange={(v) => handleNodeDataChange('querySource', v)}
                              disabled={!isOwner}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select source" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="user-input">User Input</SelectItem>
                                <SelectItem value="last-output">Last Output</SelectItem>
                                <SelectItem value="custom">Custom</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {String(selectedNode.data.querySource || 'user-input') === 'custom' && (
                            <div>
                              <Label>Custom Query</Label>
                              <Textarea
                                value={String(selectedNode.data.customQuery || '')}
                                onChange={(e) => handleNodeDataChange('customQuery', e.target.value)}
                                rows={4}
                                disabled={!isOwner}
                                placeholder="Enter a query..."
                              />
                            </div>
                          )}

                          <div className="space-y-2">
                            <Label>Top K ({Math.max(1, Math.min(10, Number(selectedNode.data.topK ?? 5)))})</Label>
                            <Slider
                              value={[Math.max(1, Math.min(10, Number(selectedNode.data.topK ?? 5)))]}
                              min={1}
                              max={10}
                              step={1}
                              onValueChange={(v) => handleNodeDataChange('topK', v[0] ?? 5)}
                              disabled={!isOwner}
                            />
                          </div>

                          <div className="space-y-2">
                            <Label>Threshold ({Number(selectedNode.data.threshold ?? 0.75).toFixed(2)})</Label>
                            <Slider
                              value={[Math.max(0.6, Math.min(0.9, Number(selectedNode.data.threshold ?? 0.75)))]}
                              min={0.6}
                              max={0.9}
                              step={0.01}
                              onValueChange={(v) => handleNodeDataChange('threshold', v[0] ?? 0.75)}
                              disabled={!isOwner}
                            />
                          </div>

                          <div>
                            <Label>
                              {String(selectedNode.data.domain || 'files') === 'knowledge-base'
                                ? 'Knowledge Base (required)'
                                : 'Knowledge Base (web fallback)'}
                            </Label>
                            {knowledgeBases.length === 0 ? (
                              <Input value="No Knowledge Bases found" readOnly disabled />
                            ) : (
                              <Select
                                value={
                                  knowledgeBases.some(kb => kb.id === String(selectedNode.data.kbId || ''))
                                    ? String(selectedNode.data.kbId)
                                    : ""
                                }
                                onValueChange={(value) => handleNodeDataChange('kbId', value)}
                                disabled={!isOwner}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select a Knowledge Base" />
                                </SelectTrigger>
                                <SelectContent>
                                  {knowledgeBases.map(kb => (
                                    <SelectItem key={kb.id} value={kb.id}>
                                      {kb.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                            <p className="text-xs text-muted-foreground mt-2">
                              On web, IntelligenceXP Search falls back to cloud KB search when a KB is provided.
                            </p>
                          </div>
                        </div>
                      )}

                      {(selectedNode.data.mode || 'search') === 'read' && (
                        <div className="space-y-4">
                          <div>
                            <Label>Path Source</Label>
                            <Select
                              value={String(selectedNode.data.pathSource || 'last-output')}
                              onValueChange={(v) => handleNodeDataChange('pathSource', v)}
                              disabled={!isOwner}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select path source" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="last-output">From Last Output</SelectItem>
                                <SelectItem value="manual">Manual</SelectItem>
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground mt-2">
                              If Last Output is a JSON array of search results, the first result's path is used.
                            </p>
                          </div>

                          {String(selectedNode.data.pathSource || 'last-output') === 'manual' && (
                            <div className="space-y-2">
                              <Label>Manual Path</Label>
                              <Input
                                value={String(selectedNode.data.manualPath || '')}
                                onChange={(e) => handleNodeDataChange('manualPath', e.target.value)}
                                disabled={!isOwner}
                                placeholder="C:\\Docs\\file.txt"
                              />
                              {isAgentGuardEnabled() && String(selectedNode.data.manualPath || '').trim() && (
                                <div className="text-xs">
                                  {isAgentOperationAllowed(String(selectedNode.data.manualPath || '').trim()) ? (
                                    <span className="text-muted-foreground">Allowlist check: OK</span>
                                  ) : (
                                    <span className="text-orange-600">⚠️ Allowlist check: blocked (path not in allowlist)</span>
                                  )}</div>
                              )}
                            </div>
                          )}

                          <div className="flex items-center justify-between rounded-md border p-3">
                            <div>
                              <div className="text-sm font-medium">Agent Guard</div>
                              <div className="text-xs text-muted-foreground">
                                Read mode enforces the allowlist when guard is enabled.
                              </div>
                            </div>
                            <Badge variant={isAgentGuardEnabled() ? 'default' : 'secondary'}>
                              {isAgentGuardEnabled() ? 'ON' : 'OFF'}
                            </Badge>
                          </div>

                          {isAgentGuardEnabled() && (
                            <div className="text-xs text-muted-foreground">
                              Allowlist prefixes: {getAllowlistPrefixes().length ? getAllowlistPrefixes().join(', ') : 'none'}
                            </div>
                          )}
                        </div>
                      )}

                      {(selectedNode.data.mode || 'search') === 'index' && (
                        <div className="space-y-4">
                          <div>
                            <Label>Action</Label>
                            <Select
                              value={String(selectedNode.data.indexAction || 'start')}
                              onValueChange={(v) => handleNodeDataChange('indexAction', v)}
                              disabled={!isOwner}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select action" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="start">Start</SelectItem>
                                <SelectItem value="stop">Stop</SelectItem>
                                <SelectItem value="rebuild">Rebuild</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-2">
                            <Label>Scopes</Label>
                            {(['documents', 'desktop', 'projects', 'chats'] as const).map((scope) => {
                              const checked = Array.isArray(selectedNode.data.scopes)
                                ? selectedNode.data.scopes.includes(scope)
                                : scope === 'documents';
                              return (
                                <label key={scope} className="flex items-center gap-2 text-sm">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) => {
                                      const prev = Array.isArray(selectedNode.data.scopes) ? selectedNode.data.scopes : ['documents'];
                                      const next = e.target.checked
                                        ? Array.from(new Set([...prev, scope]))
                                        : prev.filter((s: string) => s !== scope);
                                      handleNodeDataChange('scopes', next.length ? next : ['documents']);
                                    }}
                                    disabled={!isOwner}
                                  />
                                  <span className="capitalize">{scope}</span>
                                </label>
                              );
                            })}
                            {!Boolean((window as any).electronAPI?.isElectron) && (
                              <p className="text-xs text-muted-foreground mt-2">
                                Index control is desktop-only. On web this node will no-op (with a history note).
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                  ) : selectedNode.data.type === 'agentic-flow' ? (
                    <div className="space-y-5">
                      <div>
                        <Label>Agents (round-robin)</Label>
                        <div className="text-xs text-muted-foreground mt-1">
                          Agents will speak in order (Agent 1 → Agent 2 → Agent 3 …). Turns controls how many total messages are generated.
                        </div>
                        <div className="mt-3 space-y-3">
                          {(() => {
                            const agents: any[] = Array.isArray(selectedNode.data.agents)
                              ? selectedNode.data.agents
                              : [
                                  {
                                    id: 'agent1',
                                    name: 'Agent 1',
                                    persona: selectedNode.data.agent1Persona || 'You are Agent 1. Analyze and propose an initial position.',
                                    modelId: selectedNode.data.modelA || '',
                                  },
                                  {
                                    id: 'agent2',
                                    name: 'Agent 2',
                                    persona: selectedNode.data.agent2Persona || 'You are Agent 2. Challenge assumptions and ask clarifying questions.',
                                    modelId: selectedNode.data.modelB || '',
                                  },
                                ];

                            const setAgents = (next: any[]) => {
                              handleNodeDataChange('agents', next);
                              // keep legacy fields in sync (best-effort)
                              if (next[0]) handleNodeDataChange('agent1Persona', next[0].persona);
                              if (next[1]) handleNodeDataChange('agent2Persona', next[1].persona);
                              if (next[0]) handleNodeDataChange('modelA', next[0].modelId || '');
                              if (next[1]) handleNodeDataChange('modelB', next[1].modelId || '');
                            };

                            const addAgent = () => {
                              const count = Math.min(MAX_AGENTIC_AGENTS, agents.length + 1);
                              if (count === agents.length) return;
                              const id = `agent${count}`;
                              setAgents([
                                ...agents,
                                {
                                  id,
                                  name: `Agent ${count}`,
                                  persona: `You are Agent ${count}. Add a fresh perspective and build on prior turns.`,
                                  modelId: '',
                                },
                              ]);
                            };

                            const removeAgent = (idx: number) => {
                              const next = agents.filter((_, i) => i !== idx);
                              // Re-number to keep stable lane mapping predictable
                              const renumbered = next.map((a, i) => ({
                                ...a,
                                id: `agent${i + 1}`,
                                name: `Agent ${i + 1}`,
                              }));
                              setAgents(renumbered);
                            };

                            return (
                              <>
                                <div className="flex items-center justify-between gap-2">
                                  <Badge variant="outline">{agents.length} agent(s)</Badge>
                                  <div className="flex items-center gap-2">
                                    <Button type="button" size="sm" variant="outline" onClick={addAgent} disabled={!isOwner || agents.length >= MAX_AGENTIC_AGENTS}>
                                      + Add agent
                                    </Button>
                                  </div>
                                </div>

                                {agents.map((a, idx) => (
                                  <div key={a.id} className="rounded-md border p-3 space-y-3">
                                    <div className="flex items-center justify-between gap-2">
                                      <div className="font-medium text-sm">{a.name || `Agent ${idx + 1}`}</div>
                                      {agents.length > 2 && (
                                        <Button
                                          type="button"
                                          size="sm"
                                          variant="outline"
                                          onClick={() => removeAgent(idx)}
                                          disabled={!isOwner}
                                        >
                                          Remove
                                        </Button>
                                      )}
                                    </div>
                                    <div>
                                      <Label>Persona</Label>
                                      <Textarea
                                        value={String(a.persona || '')}
                                        onChange={(e) => {
                                          const next = agents.map((x, i) => (i === idx ? { ...x, persona: e.target.value } : x));
                                          setAgents(next);
                                        }}
                                        rows={3}
                                        disabled={!isOwner}
                                      />
                                    </div>

                                    {String(selectedNode.data.modelSource || 'inherit') === 'custom' && (
                                      <div className="space-y-2">
                                        <Label>Model</Label>
                                        <ModelSelector
                                          models={models}
                                          selectedModelId={String(a.modelId || '')}
                                          onModelChange={(id) => {
                                            const next = agents.map((x, i) => (i === idx ? { ...x, modelId: id } : x));
                                            setAgents(next);
                                          }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </>
                            );
                          })()}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Turns ({Math.max(1, Math.min(MAX_AGENTIC_TURNS, Number(selectedNode.data.rounds ?? 3)))})</Label>
                        <div className="text-xs text-muted-foreground">Total agent messages to generate this run.</div>
                        <Slider
                          value={[Math.max(1, Math.min(MAX_AGENTIC_TURNS, Number(selectedNode.data.rounds ?? 3)))]}
                          min={1}
                          max={MAX_AGENTIC_TURNS}
                          step={1}
                          onValueChange={(v) => handleNodeDataChange('rounds', v[0] ?? 3)}
                          disabled={!isOwner}
                        />
                      </div>

                      <div>
                        <Label>Models</Label>
                        <Select
                          value={String(selectedNode.data.modelSource || 'inherit')}
                          onValueChange={(v) => handleNodeDataChange('modelSource', v)}
                          disabled={!isOwner}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Model source" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="inherit">Inherit from upstream Model Selector</SelectItem>
                            <SelectItem value="custom">Choose per agent</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="rounded-md border p-3 space-y-4">
                        <div className="text-sm font-medium">Retrieval</div>

                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <div className="text-sm">Use local files via RuVector (desktop only)</div>
                            <div className="text-xs text-muted-foreground">On web, this will be disabled and fallback KB (if set) will be used.</div>
                          </div>
                          <Switch
                            checked={Boolean(selectedNode.data.retrieval?.useLocal ?? true)}
                            onCheckedChange={(checked) =>
                              handleNodeDataChange('retrieval', { ...(selectedNode.data.retrieval || {}), useLocal: checked })
                            }
                            disabled={!isOwner || !Boolean((window as any).electronAPI?.isElectron)}
                          />
                        </div>

                        <div>
                          <Label>Fallback Knowledge Base</Label>
                          {knowledgeBases.length === 0 ? (
                            <Input value="No Knowledge Bases found" readOnly disabled />
                          ) : (
                            <Select
                              value={
                                knowledgeBases.some(kb => kb.id === String(selectedNode.data.retrieval?.kbId || ''))
                                  ? String(selectedNode.data.retrieval?.kbId)
                                  : ''
                              }
                              onValueChange={(value) =>
                                handleNodeDataChange('retrieval', { ...(selectedNode.data.retrieval || {}), kbId: value })
                              }
                              disabled={!isOwner}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select a Knowledge Base" />
                              </SelectTrigger>
                              <SelectContent>
                                {knowledgeBases.map(kb => (
                                  <SelectItem key={kb.id} value={kb.id}>
                                    {kb.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label>
                            Top K ({Math.max(1, Math.min(10, Number(selectedNode.data.retrieval?.topK ?? 5)))})
                          </Label>
                          <Slider
                            value={[Math.max(1, Math.min(10, Number(selectedNode.data.retrieval?.topK ?? 5)))]}
                            min={1}
                            max={10}
                            step={1}
                            onValueChange={(v) =>
                              handleNodeDataChange('retrieval', { ...(selectedNode.data.retrieval || {}), topK: v[0] ?? 5 })
                            }
                            disabled={!isOwner}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>
                            Threshold ({Number(selectedNode.data.retrieval?.threshold ?? 0.75).toFixed(2)})
                          </Label>
                          <Slider
                            value={[Math.max(0.6, Math.min(0.9, Number(selectedNode.data.retrieval?.threshold ?? 0.75)))]}
                            min={0.6}
                            max={0.9}
                            step={0.01}
                            onValueChange={(v) =>
                              handleNodeDataChange('retrieval', { ...(selectedNode.data.retrieval || {}), threshold: v[0] ?? 0.75 })
                            }
                            disabled={!isOwner}
                          />
                        </div>

                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <div className="text-sm">Show sources/snippets with each agent message</div>
                            <div className="text-xs text-muted-foreground">Displayed in Agent Theater under each turn.</div>
                          </div>
                          <Switch
                            checked={Boolean(selectedNode.data.retrieval?.showSources ?? true)}
                            onCheckedChange={(checked) =>
                              handleNodeDataChange('retrieval', { ...(selectedNode.data.retrieval || {}), showSources: checked })
                            }
                            disabled={!isOwner}
                          />
                        </div>

                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <div className="text-sm">Auto-start local indexing if empty</div>
                            <div className="text-xs text-muted-foreground">Desktop only.</div>
                          </div>
                          <Switch
                            checked={Boolean(selectedNode.data.retrieval?.autoIndex ?? false)}
                            onCheckedChange={(checked) =>
                              handleNodeDataChange('retrieval', { ...(selectedNode.data.retrieval || {}), autoIndex: checked })
                            }
                            disabled={!isOwner || !Boolean((window as any).electronAPI?.isElectron)}
                          />
                        </div>
                      </div>

                      <div>
                        <Label>Output</Label>
                        <Select
                          value={String(selectedNode.data.returnMode || 'last')}
                          onValueChange={(v) => handleNodeDataChange('returnMode', v)}
                          disabled={!isOwner}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Return mode" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="last">Last agent message</SelectItem>
                            <SelectItem value="transcript">Full transcript</SelectItem>
                            <SelectItem value="summary">Summary</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {!blueprint?.is_private && (
                        <p className="text-orange-500 text-sm">
                          ⚠️ Public blueprints: retrieval uses the runner's permissions and local files (desktop) never leave the device.
                        </p>
                      )}
                    </div>

                  ) : selectedNode.data.type === 'user-decision' ? (
                    <div className="space-y-4">
                      <div>
                        <Label>Prompt text</Label>
                        <Textarea
                          value={String(selectedNode.data.prompt || '')}
                          onChange={(e) => handleNodeDataChange('prompt', e.target.value)}
                          rows={4}
                          disabled={!isOwner}
                        />
                      </div>
                      <div>
                        <Label>Variable name</Label>
                        <Input
                          value={String(selectedNode.data.variable || '')}
                          onChange={(e) => handleNodeDataChange('variable', e.target.value)}
                          disabled={!isOwner}
                          placeholder="decisionInput"
                        />
                        <p className="text-xs text-muted-foreground mt-2">
                          The value will be stored in <span className="font-mono">variables[variable]</span> and also set as <span className="font-mono">lastOutput</span>.
                        </p>
                      </div>
                      <div className="flex items-center justify-between gap-3 rounded-md border p-3">
                        <div>
                          <div className="text-sm font-medium">Allow empty input</div>
                          <div className="text-xs text-muted-foreground">If enabled, the run dialog will show "Skip".</div>
                        </div>
                        <Switch
                          checked={Boolean(selectedNode.data.allowEmpty)}
                          onCheckedChange={(checked) => handleNodeDataChange('allowEmpty', checked)}
                          disabled={!isOwner}
                        />
                      </div>
                    </div>

                  ) : selectedNode.data.type === 'action' ? (
                    <div className="space-y-4">
                      <div>
                        <Label>Action type</Label>
                        <Select
                          value={String(selectedNode.data.actionType || 'json-extract')}
                          onValueChange={(v) => handleNodeDataChange('actionType', v)}
                          disabled={!isOwner}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select action" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="json-extract">Extract JSON property</SelectItem>
                            <SelectItem value="text-template">Text template</SelectItem>
                            <SelectItem value="text-split">Split text</SelectItem>
                            <SelectItem value="list-join">Join list</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-2">
                          Action nodes transform the previous node&apos;s <span className="font-mono">lastOutput</span>.
                        </p>
                      </div>

                      {String(selectedNode.data.actionType || 'json-extract') === 'json-extract' ? (
                        <div>
                          <Label>JSON Path</Label>
                          <Input
                            value={String(selectedNode.data.jsonPath || '')}
                            onChange={(e) => handleNodeDataChange('jsonPath', e.target.value)}
                            placeholder="e.g. current_condition[0].temp_C"
                            disabled={!isOwner}
                          />
                        </div>
                      ) : null}

                      {String(selectedNode.data.actionType || 'json-extract') === 'text-template' ? (
                        <div>
                          <Label>Template</Label>
                          <Textarea
                            value={String(selectedNode.data.template || '')}
                            onChange={(e) => handleNodeDataChange('template', e.target.value)}
                            rows={4}
                            disabled={!isOwner}
                          />
                        </div>
                      ) : null}

                      {String(selectedNode.data.actionType || 'json-extract') === 'text-split' ? (
                        <div>
                          <Label>Delimiter</Label>
                          <Input
                            value={String(selectedNode.data.delimiter || '\n')}
                            onChange={(e) => handleNodeDataChange('delimiter', e.target.value)}
                            placeholder="Delimiter"
                            disabled={!isOwner}
                          />
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <Label>Content</Label>
                        <Textarea
                          value={String(selectedNode.data.content || '')}
                          onChange={(e) => handleNodeDataChange('content', e.target.value)}
                          rows={8}
                          disabled={!isOwner || selectedNode.id === 'start' || selectedNode.id === 'end'}
                        />
                      </div>
                    </div>
                  )}

                  {isOwner && selectedNode.id !== 'start' && selectedNode.id !== 'end' ? (
                    <Button
                      variant="destructive"
                      onClick={() => {
                        setNodes((nds) => nds.filter((node) => node.id !== selectedNode.id));
                        setEdges((eds) => eds.filter((edge) => edge.source !== selectedNode.id && edge.target !== selectedNode.id));
                        setSelectedNodeId(null);
                        setIsDirty(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4 mr-2" /> Delete Node
                    </Button>
                  ) : null}
                </CardContent>
              </Card>
            </aside>
          )}
        </main>
      </div>

      <ContextMenu onOpenChange={(open) => { if (!open) setMenu(null); }}>
        <ContextMenuTrigger asChild>
          <button
            ref={contextMenuTriggerRef}
            type="button"
            className="sr-only"
            aria-hidden="true"
            tabIndex={-1}
            style={menu ? { position: 'fixed', left: menu.x, top: menu.y } : { position: 'fixed', left: -9999, top: -9999 }}
          >
            Open blueprint context menu
          </button>
        </ContextMenuTrigger>
        <ContextMenuContent className="w-48">
          <ContextMenuItem onClick={onDeleteSelected} disabled={!menu || !isOwner}>
            <Trash2 className="h-4 w-4 mr-2" /> Delete
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>

      <Dialog open={aiBuildOpen} onOpenChange={setAiBuildOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>AI Build</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ai-build-prompt">Describe the blueprint you want</Label>
              <Textarea
                id="ai-build-prompt"
                value={aiBuildPrompt}
                onChange={(event) => setAiBuildPrompt(event.target.value)}
                placeholder="Example: Build me a blueprint that fetches the weather and then discuss it with another agent"
                rows={6}
              />
              <p className="text-sm text-muted-foreground">
                Supported right now: weather + agent discussion, multi-agent debates, and MapleClaw-enabled local orchestration.
              </p>
            </div>
            <div className="flex items-center justify-between rounded-lg border bg-muted/20 px-4 py-3">
              <div className="space-y-1">
                <Label htmlFor="auto-evolve-toggle">Auto Evolve after each run</Label>
                <p className="text-sm text-muted-foreground">Let the blueprint keep proposing and applying graph changes after the first run completes.</p>
              </div>
              <Switch id="auto-evolve-toggle" checked={autoEvolveEnabled} onCheckedChange={setAutoEvolveEnabled} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiBuildOpen(false)}>Cancel</Button>
            <Button onClick={() => void buildBlueprintFromPrompt()}>Build Blueprint</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={runDialogOpen} onOpenChange={setRunDialogOpen}>
        <DialogContent className="w-[96vw] max-w-[1600px] h-[92vh] p-0 overflow-hidden flex flex-col">
          <DialogHeader className="border-b px-6 py-4 shrink-0">
            <div className="flex items-center justify-between gap-4">
              <div>
                <DialogTitle>Run Blueprint</DialogTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Live theater, orchestration, and raw execution details for this blueprint run.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <Badge variant={executionResult?.error ? 'destructive' : runIsPaused ? 'outline' : isExecuting ? 'secondary' : 'outline'}>
                  {executionResult?.error ? 'failed' : runIsPaused ? 'paused' : isExecuting ? 'running' : executionResult ? 'completed' : 'idle'}
                </Badge>
                {executionResult ? (
                  <Badge variant="outline">{(executionResult.history || []).length} events</Badge>
                ) : null}
                {completedRunRecord ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedRun(completedRunRecord);
                      setRunsOpen(true);
                      void loadRuns();
                    }}
                  >
                    Open latest saved run
                  </Button>
                ) : null}
              </div>
            </div>
          </DialogHeader>

          <ResizablePanelGroup direction="horizontal" className="flex-1 min-h-0">
            <ResizablePanel defaultSize={28} minSize={22} maxSize={36}>
              <ScrollArea className="h-full">
                <div className="space-y-4 p-4">
                  <div className="space-y-2">
                    <Label htmlFor="run-input">Initial input</Label>
                    <Textarea
                      id="run-input"
                      value={runInitialInput}
                      onChange={(event) => setRunInitialInput(event.target.value)}
                      rows={8}
                      placeholder="Describe what you want this blueprint to do"
                    />
                  </div>

                  <div className="flex items-center justify-between rounded-lg border px-4 py-3">
                    <div>
                      <div className="font-medium">Skip Trust for this blueprint</div>
                      <div className="text-xs text-muted-foreground">Use the local MapleClaw / Blueprint runtime without Trust pre-checks.</div>
                    </div>
                    <Switch checked={blueprintTrustDisabled} onCheckedChange={setBlueprintTrustDisabled} />
                  </div>

                  <div className="flex items-center justify-between rounded-lg border px-4 py-3">
                    <div>
                      <div className="font-medium">Auto-open 3D Cortex</div>
                      <div className="text-xs text-muted-foreground">Launch the live orchestration visualizer as the run starts.</div>
                    </div>
                    <Switch checked={autoLoad3DCortexOnRun} onCheckedChange={setAutoLoad3DCortexOnRun} />
                  </div>

                  <div className="flex gap-2">
                    <Button className="flex-1" onClick={() => void executeFlow()} disabled={isExecuting || !runInitialInput.trim()}>
                      {isExecuting ? 'Running…' : 'Run now'}
                    </Button>
                    <Button variant="outline" onClick={() => setRunDialogOpen(false)} disabled={isExecuting}>Close</Button>
                  </div>

                  {executionResult?.awaiting?.type === 'user-decision' && (
                    <div className="space-y-2 rounded-lg border p-3">
                      <Label>{executionResult.awaiting.prompt}</Label>
                      <Textarea value={decisionValue} onChange={(event) => setDecisionValue(event.target.value)} rows={4} />
                      <div className="flex gap-2">
                        <Button className="flex-1" onClick={() => void resumeFromDecision(decisionValue)} disabled={isExecuting}>Continue</Button>
                        {executionResult.awaiting.allowEmpty && (
                          <Button variant="outline" onClick={() => void resumeFromDecision('')} disabled={isExecuting}>Skip</Button>
                        )}
                      </div>
                    </div>
                  )}

                  {executionResult?.awaiting?.type === 'manual-pause' && (
                    <div className="space-y-2 rounded-lg border p-3 text-sm text-muted-foreground">
                      <p>{executionResult.awaiting.reason || 'Execution is paused.'}</p>
                      <Button onClick={() => void resumeFromDecision('')} disabled={isExecuting}>Resume</Button>
                    </div>
                  )}

                  {executionResult?.lastAgenticNodeId && !executionResult?.awaiting && (
                    <div className="space-y-2 rounded-lg border p-3">
                      <Label>Follow-up message</Label>
                      <Textarea value={followupText} onChange={(event) => setFollowupText(event.target.value)} rows={4} placeholder="Continue the conversation with the latest agentic flow" />
                      <div className="flex items-center gap-2">
                        <Input type="number" min={1} max={10} value={followupRounds} onChange={(event) => setFollowupRounds(Number(event.target.value || 1))} />
                        <Button className="flex-1" onClick={() => void submitFollowup()} disabled={isExecuting || !followupText.trim()}>Send follow-up</Button>
                      </div>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </ResizablePanel>

            <ResizableHandle withHandle />

            <ResizablePanel defaultSize={72} minSize={50}>
              <Tabs defaultValue="theater" className="flex h-full flex-col">
                <div className="border-b px-4 py-3 shrink-0 flex items-center justify-between gap-3">
                  <TabsList>
                    <TabsTrigger value="theater">Theater</TabsTrigger>
                    <TabsTrigger value="details">Details</TabsTrigger>
                  </TabsList>
                  {executionResult?.finalOutput ? (
                    <span className="text-xs text-muted-foreground">Final output ready</span>
                  ) : executionResult ? (
                    <span className="text-xs text-muted-foreground">Streaming execution history</span>
                  ) : null}
                </div>

                <TabsContent value="theater" className="flex-1 min-h-0 m-0 p-4">
                  {executionResult ? (
                    <div className="h-full overflow-hidden rounded-lg border bg-background">
                      <AgentTheater
                        nodes={displayNodes as any}
                        history={normalizeRunHistory(executionResult.history || []) as any}
                        onActiveNodeIdChange={setActiveTheaterNodeId}
                        mode={isExecuting || runIsPaused ? 'live' : 'saved'}
                        scoutEnabled={scoutEnabled}
                        showScoutToggle={true}
                        onScoutEnabledChange={setScoutEnabled}
                        scoutKeywords={scoutKeywords}
                        onScoutKeywordsChange={setScoutKeywords}
                        autoOpenSingularity={autoLoad3DCortexOnRun}
                      />
                    </div>
                  ) : (
                    <div className="flex h-full items-center justify-center rounded-lg border border-dashed text-sm text-muted-foreground px-6 text-center">
                      Start a run to open the full Agent Theater and Cortex orchestration view.
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="details" className="flex-1 min-h-0 m-0 p-4">
                  <div className="h-full min-h-0 overflow-hidden rounded-lg border">
                    <ScrollArea className="h-full p-4">
                      {executionResult ? (
                        <div className="space-y-4">
                          {executionResult.error ? (
                            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
                              {executionResult.error}
                            </div>
                          ) : null}
                          {executionResult.finalOutput ? (
                            <Card className="border-border/70 bg-card/95 shadow-sm">
                              <CardHeader>
                                <CardTitle className="text-base text-foreground">Final output</CardTitle>
                              </CardHeader>
                              <CardContent className="space-y-4">
                                <div className="prose prose-sm max-w-none break-words text-foreground prose-p:leading-7 prose-headings:text-foreground prose-strong:text-foreground prose-code:text-foreground prose-li:text-foreground prose-a:text-primary dark:prose-invert">
                                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {String(executionResult.finalOutput)}
                                  </ReactMarkdown>
                                </div>

                                {executionResult.finalOutput && !executionResult.awaiting ? (
                                  <div className="rounded-lg border border-border/70 bg-background/80 p-4 shadow-sm">
                                    <div className="mb-3 space-y-1">
                                      <div className="text-sm font-medium text-foreground">Reply to this output</div>
                                      <p className="text-xs text-muted-foreground">
                                        {executionResult.lastAgenticNodeId
                                          ? 'If the AI is asking a question, answer here to continue the conversation.'
                                          : 'Answer here to run the blueprint again using your reply as the next input.'}
                                      </p>
                                    </div>
                                    <div className="space-y-3">
                                      <Textarea
                                        value={followupText}
                                        onChange={(event) => setFollowupText(event.target.value)}
                                        rows={4}
                                        placeholder="Type your reply to the AI..."
                                      />
                                      <div className="flex items-center gap-2">
                                        <Input
                                          type="number"
                                          min={1}
                                          max={10}
                                          value={followupRounds}
                                          onChange={(event) => setFollowupRounds(Number(event.target.value || 1))}
                                          className="w-24"
                                        />
                                        <Button
                                          className="flex-1"
                                          onClick={() => void submitFollowup()}
                                          disabled={isExecuting || !followupText.trim()}
                                        >
                                          {executionResult.lastAgenticNodeId ? 'Reply and continue' : 'Reply and run again'}
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                ) : null}
                              </CardContent>
                            </Card>
                          ) : null}
                          <div className="space-y-3">
                            {(executionResult.history || []).map((entry, index) => (
                              <div key={`${entry.timestamp}-${index}`} className="rounded-lg border border-border/70 bg-card/80 p-3 shadow-sm">
                                <div className="mb-2 flex flex-wrap items-center gap-2 text-xs uppercase tracking-wide text-foreground/65">
                                  <Badge variant="outline">{entry.role || 'system'}</Badge>
                                  <span>{entry.nodeId}</span>
                                  <span>{new Date(entry.timestamp).toLocaleTimeString()}</span>
                                </div>
                                <p className="whitespace-pre-wrap text-sm leading-6 text-foreground">{entry.message}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                          Run the blueprint to inspect live history and MapleClaw responses.
                        </div>
                      )}
                    </ScrollArea>
                  </div>
                </TabsContent>
              </Tabs>
            </ResizablePanel>
          </ResizablePanelGroup>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showLeaveConfirmDialog} onOpenChange={setShowLeaveConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved blueprint changes. You can stay here to keep editing or leave without saving.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Stay</AlertDialogCancel>
            <AlertDialogAction onClick={closeEmbeddedOrNavigateHome}>Leave anyway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {blueprint && blueprintId && (
        <ShareBlueprintDialog
          open={showShareDialog}
          onOpenChange={setShowShareDialog}
          blueprintId={blueprintId}
          blueprintName={blueprint.name}
          organizations={organizations}
          currentOrganizationId={(blueprint as any).organization_id || null}
          onShareWithOrganization={handleShareWithOrganization}
          embeddedInMapleOS={embeddedInMapleOS}
        />
      )}

      <Dialog open={runsOpen} onOpenChange={setRunsOpen}>
        <DialogContent className="w-[96vw] max-w-[1600px] h-[92vh] p-0 overflow-hidden flex flex-col bg-gradient-to-br from-background via-background to-background/95">
          <DialogHeader className="shrink-0 border-b border-border/50 p-6 bg-gradient-to-r from-muted/30 to-muted/10 backdrop-blur-sm">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-2 rounded-xl bg-primary/10 border border-primary/20 shrink-0">
                  <Cpu className="h-6 w-6 text-primary" />
                </div>
                <div className="min-w-0">
                  <DialogTitle className="text-xl font-bold bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text">
                    Blueprint runs
                  </DialogTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Inspect the latest run, open the full theater playback, and jump into the saved Cortex orchestration view.
                  </p>
                </div>
              </div>
              {selectedRun ? (
                <div className="flex items-center gap-2 flex-wrap justify-end rounded-2xl border-2 border-border bg-background/80 px-4 py-2 backdrop-blur-sm shadow-lg">
                  <Badge variant={selectedRun.status === 'failed' ? 'destructive' : selectedRun.status === 'completed' ? 'secondary' : 'outline'}>
                    {selectedRun.status || 'completed'}
                  </Badge>
                  <Badge variant="outline">{getRunHistoryCount(selectedRun)} events</Badge>
                  {Array.isArray(selectedRun.nodes_snapshot) ? (
                    <Badge variant="outline">{selectedRun.nodes_snapshot.length} nodes</Badge>
                  ) : null}
                </div>
              ) : null}
            </div>
          </DialogHeader>

          <ResizablePanelGroup direction="horizontal" className="flex-1 min-h-0">
            <ResizablePanel defaultSize={30} minSize={22} maxSize={38}>
              <div className="h-full border-r border-border/50 flex flex-col min-h-0 bg-gradient-to-b from-background to-background/90">
                <div className="shrink-0 border-b border-border/50 p-4 bg-gradient-to-r from-muted/20 to-muted/5">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-foreground/90">Runs</div>
                      <div className="text-xs text-muted-foreground">Saved blueprint playbacks</div>
                    </div>
                    <Button variant="outline" size="sm" className="rounded-xl border-border/60 bg-background/80 shadow-sm hover:bg-background" onClick={() => void loadRuns()}>
                      Refresh
                    </Button>
                  </div>
                </div>
                <ScrollArea className="flex-1">
                  <div className="space-y-4 p-4">
                    {runs.length === 0 ? (
                      <Card className="border-border/50 bg-card/50 backdrop-blur-sm shadow-lg">
                        <CardContent className="py-10 text-center text-sm text-muted-foreground">
                          No runs have been recorded yet.
                        </CardContent>
                      </Card>
                    ) : (
                      runs.map((run, index) => {
                        const isSelected = selectedRun?.id === run.id;
                        return (
                          <button
                            key={run.id}
                            type="button"
                            onClick={() => setSelectedRun(run)}
                            className={cn(
                              'group relative w-full overflow-hidden rounded-xl border-2 text-left transition-all duration-300 hover:scale-[1.01] hover:shadow-lg',
                              isSelected
                                ? 'border-primary ring-4 ring-primary/20 shadow-lg bg-primary/5'
                                : 'border-border/30 bg-card/40 hover:border-primary/50 hover:bg-card/70'
                            )}
                          >
                            <div className="p-4">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="font-semibold text-base truncate">{run.title || 'Untitled run'}</div>
                                  <div className="text-xs text-muted-foreground mt-2">{new Date(run.created_at).toLocaleString()}</div>
                                </div>
                                <div className="flex flex-col items-end gap-2 shrink-0">
                                  {index === 0 ? <Badge variant="outline">Latest</Badge> : null}
                                  <Badge variant={run.status === 'failed' ? 'destructive' : run.status === 'completed' ? 'secondary' : 'outline'}>
                                    {run.status || 'completed'}
                                  </Badge>
                                </div>
                              </div>
                              <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                                <span>{getRunHistoryCount(run)} events</span>
                                {run.last_cursor ? <span>• cursor {run.last_cursor}</span> : null}
                              </div>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </ScrollArea>
              </div>
            </ResizablePanel>

            <ResizableHandle withHandle />

            <ResizablePanel defaultSize={70} minSize={45}>
              {selectedRun ? (
                <Tabs defaultValue="theater" className="flex h-full flex-col bg-gradient-to-br from-background via-background to-background/95">
                  <div className="shrink-0 border-b border-border/50 p-6 bg-gradient-to-r from-muted/20 to-muted/5 space-y-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <h3 className="text-2xl font-bold tracking-tight truncate">{selectedRun.title || 'Untitled run'}</h3>
                        <p className="text-sm text-muted-foreground mt-2">
                          Started {new Date(selectedRun.created_at).toLocaleString()} • Updated {new Date(selectedRun.updated_at).toLocaleString()}
                        </p>
                      </div>
                      <TabsList className="grid grid-cols-2 h-12 p-1 bg-muted/30 backdrop-blur-sm border border-border/50 rounded-xl">
                        <TabsTrigger value="theater" className="px-5 data-[state=active]:bg-background data-[state=active]:shadow-md rounded-lg">
                          Theater
                        </TabsTrigger>
                        <TabsTrigger value="history" className="px-5 data-[state=active]:bg-background data-[state=active]:shadow-md rounded-lg">
                          History
                        </TabsTrigger>
                      </TabsList>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <Card className="border-border/50 bg-card/50 backdrop-blur-sm shadow-lg">
                        <CardHeader className="pb-4">
                          <CardTitle className="text-sm">Initial input</CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm text-muted-foreground whitespace-pre-wrap break-words">
                          {selectedRun.initial_input || 'No initial input was saved for this run.'}
                        </CardContent>
                      </Card>
                      <Card className="border-border/50 bg-card/50 backdrop-blur-sm shadow-lg">
                        <CardHeader className="pb-4">
                          <CardTitle className="text-sm">Last event</CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm text-muted-foreground whitespace-pre-wrap break-words">
                          {getRunLastMessage(selectedRun) || 'No history was saved for this run.'}
                        </CardContent>
                      </Card>
                    </div>
                  </div>

                  <TabsContent value="theater" className="flex-1 min-h-0 m-0 p-6">
                    <Card className="h-full border-border/50 bg-card/50 backdrop-blur-sm shadow-lg overflow-hidden">
                      <CardContent className="h-full p-0">
                        <AgentTheater
                          nodes={getRunSnapshotNodes(selectedRun, nodes) as any}
                          history={normalizeRunHistory(selectedRun.history || []) as any}
                          mode="saved"
                          cortexMode="saved"
                        />
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="history" className="flex-1 min-h-0 m-0 p-6">
                    <Card className="h-full border-border/50 bg-card/50 backdrop-blur-sm shadow-lg overflow-hidden">
                      <CardContent className="h-full p-0">
                        <ScrollArea className="h-full p-4">
                          <div className="space-y-3">
                            {normalizeRunHistory(selectedRun.history || []).length === 0 ? (
                              <div className="text-sm text-muted-foreground">No history was saved for this run.</div>
                            ) : (
                              normalizeRunHistory(selectedRun.history || []).map((entry: any, index: number) => (
                                <div key={`${entry.timestamp}-${index}`} className="rounded-xl border border-border/50 bg-card/40 p-4 shadow-sm">
                                  <div className="mb-2 flex flex-wrap items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
                                    <Badge variant="outline">{entry.role || 'system'}</Badge>
                                    <span>{entry.nodeId}</span>
                                    <span>{new Date(entry.timestamp).toLocaleTimeString()}</span>
                                  </div>
                                  <p className="whitespace-pre-wrap text-sm leading-6">{entry.message}</p>
                                </div>
                              ))
                            )}
                          </div>
                        </ScrollArea>
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
              ) : (
                <div className="flex h-full items-center justify-center p-6 bg-gradient-to-br from-background via-background to-background/95">
                  <Card className="w-full max-w-xl border-border/50 bg-card/50 backdrop-blur-sm shadow-lg">
                    <CardContent className="py-12 text-center text-sm text-muted-foreground">
                      Select a run to inspect the full playback and orchestration details.
                    </CardContent>
                  </Card>
                </div>
              )}
            </ResizablePanel>
          </ResizablePanelGroup>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlueprintCanvas;
